import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import {
  AlertController,
  IonicModule,
  LoadingController,
  ModalController,
  NavController,
  PopoverController,
} from '@ionic/angular';
import Autolinker from 'autolinker';
import { Data } from 'emoji-mart';
import _ from 'lodash';
import moment from 'moment';
import { NgxTippyModule, NgxTippyProps } from 'ngx-tippy-wrapper';
import { Subject, Subscription } from 'rxjs';
import { validate as validateUuid } from 'uuid';

import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import {
  ApprovalRequest,
  Automation,
  AutomationRun,
  CollaboratorType,
  Conversation,
  ConversationCollaborator,
  ConversationMessage,
  Escalation,
  InboundEmail,
  KnowledgeBaseArticle,
  PriorityTokens,
  Profile,
  Recipient,
  Tag,
  CustomField,
} from 'src/models';
import { AGENT_CONFIDENCE_THRESHOLD } from 'src/models/ai-tester.js';
import {
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS,
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS,
  CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_MAP,
} from 'src/models/conversation-links';
import { ColonToEmojiPipe } from 'src/pipes/colonToEmoji.pipe';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AITesterService, ASPECT_FORMATS, AgentActivity } from 'src/services/ai-tester.service.js';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { AutomationsService } from 'src/services/automations.service';
import { CollaboratorTypesService } from 'src/services/collaborator-types.service';
import { ConversationsService } from 'src/services/conversations.service';
import { CustomCommandBarService } from 'src/services/custom-command-bar.service'; // Ensure this is imported
import { EmojisService } from 'src/services/emojis.service';
import { EscalationsService } from 'src/services/escalations.service';
import { InboundEmailrulesService } from 'src/services/inbound-email-rules.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { ServerEventSubscription, ServerEventsService } from 'src/services/server-events.service';
import { SlackService } from 'src/services/slack.service';
import { TagsService } from 'src/services/tags.service';
import { TokenService } from 'src/services/token.service.js';
import { UsersService } from 'src/services/users.service';

// Import CustomCommandBarService (if not already via other means, though it should be providedIn: 'root')
// import { ConversationCommandService } from 'src/services/conversation-command.service'; // Remove this

import { AutomationLogsComponent } from '../automations/automation-logs/automation-logs.component';
import { ActionDropdownComponent, ActionDropdownType } from '../components/action-dropdown/action-dropdown.component';
import { ApprovalRequestComponent } from '../components/approval-request/approval-request.component';
import { AvatarModule } from '../components/avatar/avatar.module';
import { ConversationChipComponent } from '../components/conversation-chip/conversation-chip.component';
import { ConversationInfoComponent } from '../components/conversation-info/conversation-info.component';
import { ConversationListComponent } from '../components/conversation-list/conversation-list.component';
import { CustomFieldsModule } from '../components/custom-fields/custom-fields.module';
import { ResponseInputModule } from '../components/custom-fields/response-input/response-input.module';
import { EmojiPickerComponent } from '../components/emoji-picker/emoji-picker.component';
import { InlineFileAttachmentComponent } from '../components/inline-file-attachment/inline-file-attachment.component';
import { KbSearchComponent } from '../components/kb-search/kb-search.component';
import { LinkExternalTaskModalComponent } from '../components/link-external-task-modal/link-external-task-modal.component';
import { ReplyComposerComponent } from '../components/reply-composer/reply-composer.component';
import { SlackMarkdown } from '../components/slack-blocks/slack-markdown';
import { SlackMessageComponent } from '../components/slack-message/slack-message.component';
import { SnoozeComponent } from '../components/snooze/snooze.component';
import { TextPopoverComponent } from '../components/text-popover/text-popover.component';
import {
  TicketTypeFieldsComponent,
  CustomFieldValues,
} from '../components/ticket-type-fields/ticket-type-fields.component';
import { TriageComponent } from '../components/triage/triage.component';
import { TriggerManualAutomationModalComponent } from '../components/trigger-manual-automation-modal/trigger-manual-automation-modal.component';
import {
  CONSUMER_EMAIL_DOMAINS,
  CONVERSATION_PRIORITY_LABEL_MAP,
  CONVERSATION_PRIORITY_VALUES,
  CONVERSATION_STATUS,
  DEFAULT_PRIORITY_TOKENS as DEFAULT_PRIORITY_TOKENS,
  ESCALATION_STATUSES,
  ESCALATION_STATUS_LABELS,
  EXTERNAL_TASK_MANAGEMENT_NAMES,
  MESSAGE_SOURCE_TYPE,
  STATUS_COLUMN_FORMAT_MAP,
  SUPPORT_STEP_ASSIGNEE_TYPES,
  TEMPLATE_TYPE,
  routes,
} from '../constants';
import {
  createPriorityValueToTokenMap,
  delay,
  downloadSlackAttachment,
  formatSlackContent,
  generateCustomFieldsArray,
  getColorForConversationPriority,
  getDisplayTextForDuration,
  getEmailDomain,
  getHtmlFromSlackMd,
  getIconForConversationPriority,
  getSlackLink,
  isDev,
  transformMessage,
  isUrl as isURL,
  formatLink, // Import and alias to isURL if templates use that casing
} from '../utils/utils';

import { ConversationLinksListComponent } from './conversation-links-list/conversation-links-list.component';
import { RecipientsPopoverComponent } from './email-recipients-popover/email-recipients-popover.component';
const NO_REPLY_EMAILS = ['no-reply', 'noreply', 'donotreply', 'do-not-reply'];

function isDoNotReplyEmail(email: string) {
  return NO_REPLY_EMAILS.some((noReply) => email.toLowerCase().trim().startsWith(noReply));
}

const labelDeleteMessage = 'Delete message';
const labelMarkAsResolvingComment = 'Mark as resolving comment';
const labelAddAsTemplate = 'Create reusable template from message';
const labelAskKb = 'Ask AI for answer';
const labelViewAgentThoughts = 'View agent thoughts';
const labelSplitConversation = 'Split into new conversation from here';
const labelAddToKb = 'Add to knowledge base';

interface TimelineEvent {
  type: string;
  timestamp: Date;
  isExpanded?: boolean;
  data: any;
}

// interface delinating between what we get from the API and what we display
interface ConversationDetailsMessage extends ConversationMessage {
  aiThoughts?: AgentActivity[];
  aiThoughtsShowLowConfidence?: boolean;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    RouterModule,
    CustomFieldsModule,
    AvatarModule,
    NgxTippyModule,
    SharedDirectivesModule,
    SharedPipesModule,
    ConversationListComponent,
    InlineFileAttachmentComponent,
    ReplyComposerComponent,
    KbSearchComponent,
    ResponseInputModule,
    SlackMessageComponent,
    ConversationLinksListComponent,
    TicketTypeFieldsComponent,
    ConversationChipComponent,
    ColonToEmojiPipe,
    ConversationInfoComponent,
  ],
  selector: 'app-conversation-details',
  templateUrl: './conversation-details.component.html',
  styleUrls: ['./conversation-details.component.scss'],
})
export class ConversationDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('composer') private composer: ReplyComposerComponent;
  @ViewChild('content') private content: any;
  @ViewChild('kbSearch') private kbSearch: KbSearchComponent;
  @ViewChild('recipientsPopover') recipientsPopover!: TemplateRef<any>;

  conversationId: string;

  messages: Partial<ConversationDetailsMessage>[];
  conversation: Partial<Conversation>;
  auditLog: any[];
  timelineEvents: TimelineEvent[];
  linkedConversations: any[];
  isLoadingTimeline = false;
  isLoadingConversation = false;
  showAuditTrail: boolean;
  showAuditTrailLow: boolean;
  showTriageMessages = true;
  tags: Partial<Tag>[] = [];
  isEmailBased = false;
  isWidgetBased = false;
  isMicrosoftTeamsBased = false;
  isDashboardBased = false;
  isBotDmBased = false;
  showEmailHtmlModal = false;
  inboundEmail: any;
  isSnoozeOpen = false;
  subscription: ServerEventSubscription;
  private eventSubject = new Subject<any>();
  private eventSubscription: Subscription;
  private isFetchInProgress = false;
  pendingMessages: Record<
    string,
    { name: string; content: string; isPrivateExplanation: boolean; ts: string; attachments?: File[] }
  > = {};
  taskManagerName: string;
  manuallyShowRightCol: boolean;
  collaboratorTypes: Partial<CollaboratorType>[] = [];
  collaborators: Record<string, ConversationCollaborator> = {};
  isSidebarMode: boolean;
  showCreateApprovalRequestModal = false;
  wasManuallySplit = false;
  referralUrl: string;

  // Subscription for CustomCommandBarService (renamed from conversationCommandSubscription)
  private commandBarActionsSubscription = new Subscription();

  slackAttachments?: Record<string, { slackId: string; name: string }[]>;

  uniqueAttachmentIds = new Set<string>();

  autolinker = new Autolinker();
  slackMarkdown = new SlackMarkdown();

  @ViewChild('previousConversationsComponent') previousConversationsComponent: ConversationListComponent;
  loadedPreviousConversations = false;

  messageCompose: string;
  previousMessageCompose = '';

  isEditorFocused = false;
  isSendingReply = false;
  deletingMessageIds = new Set<string>();
  sendingMessageIds = new Set<string>();
  openTaskAccordions: string;
  openEscalationsAccordions: string;
  openAutomationsAccordions: string;
  openApprovalRequestsAccordions: string;
  similarConversations: Partial<Conversation>[] = [];
  isLoadingSimilarConversations = false;
  isConnectingUserToSlack = false;
  isLoadingTasks = false;
  submittedBy: Partial<Profile>;
  automationRuns: Partial<AutomationRun>[] = [];
  approvalRequests: Partial<ApprovalRequest>[] = [];

  hasUserChatWriteScope = false;
  hasUserReactionsWriteScope = false;
  hasUserFilesWriteScope = false;
  hasTenantFilesWriteScope = false;
  lookups: Record<string, Record<string, string>> = {};
  emojiReactionTooltipProps: Record<string, NgxTippyProps> = {};
  connectedTeams: any[];

  sidebarTab: 'details' | 'knowledge' = 'details';
  recentConversationsTab: 'user' | 'account' = 'user';
  priorityTokens: Record<string, PriorityTokens> = DEFAULT_PRIORITY_TOKENS;
  priorityValueToTokenMap: Record<number, PriorityTokens> = createPriorityValueToTokenMap(DEFAULT_PRIORITY_TOKENS);

  downloadFileId?: string;

  isInvalidConversation = false;
  disableButtons = false;

  private _allFieldDefinitions: CustomField[] = []; // Added private property

  isLoadingEscalations = false;
  escalations: Partial<Escalation>[] = [];

  tos: Recipient[] = [];
  ccs: Recipient[] = [];
  bccs: Recipient[] = [];

  conversationLinksEnabled = false;

  CONVERSATION_STATUS = CONVERSATION_STATUS;
  STATUS_COLUMN_FORMAT_MAP = STATUS_COLUMN_FORMAT_MAP;

  ASPECT_FORMATS = ASPECT_FORMATS;

  ESCALATION_STATUSES = ESCALATION_STATUSES;
  ESCALATION_STATUS_LABELS = ESCALATION_STATUS_LABELS;

  CONVERSATION_PRIORITY_VALUES = CONVERSATION_PRIORITY_VALUES;
  CONVERSATION_PRIORITY_LABEL_MAP = CONVERSATION_PRIORITY_LABEL_MAP;

  // Make the imported isURL available to the template
  public isURL = isURL;

  constructor(
    private route: ActivatedRoute,
    private navController: NavController,
    private popoverCtrl: PopoverController,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private loadingCtrl: LoadingController,
    private conversationsService: ConversationsService,
    private tagsService: TagsService,
    private msgSrvc: MessageService,
    private apiService: ApiService,
    private analyticsService: AnalyticsService,
    private serverEventsService: ServerEventsService,
    private searchablePopoverService: SearchablePopoverService,
    private automationsService: AutomationsService,
    public authService: AuthService,
    private slackService: SlackService,
    private emojisService: EmojisService,
    private collaboratorTypesService: CollaboratorTypesService,
    private inboundEmailRulesService: InboundEmailrulesService,
    private usersService: UsersService,
    private router: Router,
    private aiTesterService: AITesterService,
    private escalationsService: EscalationsService,
    private cdr: ChangeDetectorRef,
    private customCommandBarService: CustomCommandBarService,
    private tokenService: TokenService,
  ) {}

  async ngOnInit() {
    const tenant = this.authService.tenant;
    this.customCommandBarService.registerContext('conversationDetailsActive'); // Register context

    this.slackAttachments = {};
    this.taskManagerName = tenant?.externalTaskManagementConfig?.name;
    this.showAuditTrail = localStorage.getItem('showAuditTrail') === 'hidden' ? false : window.innerWidth > 540;
    this.showAuditTrailLow = localStorage.getItem('showAuditTrailLow') === 'shown' ? true : false;
    this.conversationId = this.route.snapshot.paramMap.get('conversationId');

    // Kinda hacky way of figuring out if this is in sidebar mode and not full page
    this.isSidebarMode = !window.location.pathname.includes('/conversations/');

    // this.addCommandShortcutListeners(); // Remove call to old command bar listeners
    this.loadCollaboratorTypes();

    this.downloadFileId = this.route.snapshot.queryParamMap.get('downloadFileId');

    if (this.downloadFileId) {
      await this.downloadAttachment(this.downloadFileId);
    }

    // Create a debounced processor for events
    this.eventSubscription = this.eventSubject.subscribe(async (data: any) => {
      // Skip if a fetch is already in progress
      if (this.isFetchInProgress) {
        return;
      }

      try {
        this.isFetchInProgress = true;
        const eventType = data.type;

        switch (eventType) {
          // Message-related events
          case 'message:created':
            await this.handleMessageCreated(data);
            break;
          case 'triage-message:created':
            await this.handleTriageMessageCreated(data);
            break;
          case 'message:updated':
            await this.handleMessageUpdated(data);
            break;
          case 'message:deleted':
            await this.handleMessageDeleted(data);
            break;

          // General conversation updates
          case 'conversation:updated':
            await this.handleConversationUpdated(data);
            break;
          case 'conversation:changed_sub_status':
            await this.handleStatusChanged(data);
            break;

          // Status changes (specific handling for status due to special UX)
          case 'conversation:changed_status':
            await this.handleStatusChanged(data);
            break;

          case 'conversation:unassigned':
          case 'conversation:reassigned':
            await this.handleAssignmentChanged(data);
            break;
          case 'conversation:escalated':
            await this.handleAssignmentChanged(data);
            break;

          // Customer/Account changes
          case 'conversation:changed_customer':
            await this.handleCustomerChanged(data);
            break;
          case 'conversation:changed_account':
            await this.handleAccountChanged(data);
            break;

          // Ticket type changes
          case 'conversation:changed_ticket_type':
          case 'conversation:changed_ticket_type_fields':
          case 'conversation:changed_priority':
            await this.handleConversationUpdated(data);
            break;

          // Follower/Collaborator updates
          case 'conversation:follower_user_added':
          case 'conversation:follower_team_added':
          case 'conversation:follower_user_removed':
          case 'conversation:follower_team_removed':
            await this.handleFollowersChanged(data);
            break;
          case 'conversation:collaborator_user_added':
          case 'conversation:collaborator_team_added':
          case 'conversation:collaborator_user_removed':
          case 'conversation:collaborator_team_removed':
            await this.handleCollaboratorChanged(data);
            break;

          // Triage/Workflow updates
          case 'conversation:triaged':
            await this.handleTriaged(data);
            break;
          case 'conversation:snoozed':
            await this.handleSnoozed(data);
            break;

          // Tag updates
          case 'conversation:tags_added':
          case 'conversation:tags_removed':
            await this.handleTagsChanged(data);
            break;

          // Approval requests
          case 'conversation:approval_request_created':
          case 'conversation:approval_request_approved':
          case 'conversation:approval_request_rejected':
            await this.handleApprovalRequestsChanged(data);
            break;

          // External integrations
          case 'conversation:external_task_created':
            await this.loadTasks();
            break;

          // Special cases
          case 'conversation:merged':
            await this.fetchTimeline(false, false);
            break;
          case 'conversation:deleted':
            this.handleDeleted();
            break;

          // Escalation events
          case 'conversation:escalation_created':
            await this.handleEscalationCreated(data);
            break;

          case 'conversation:escalation_updated':
          case 'conversation:escalation_acknowledged':
          case 'conversation:escalation_resolved':
            await this.handleEscalationUpdated(data);
            break;

          // Default case - fallback to full refresh
          default:
            await this.fetchTimeline(false, false);
            break;
        }
      } finally {
        this.isFetchInProgress = false;
      }
    });

    // Subscribe to server events but feed them through our debounced subject
    this.subscription = this.serverEventsService.subscribe(
      [
        // Message events
        'message:created',
        'triage-message:created',
        'message:updated',
        'message:deleted',

        // Core conversation state changes
        'conversation:updated', // General updates (ticket type, fields, priority, etc.)
        'conversation:created',
        'conversation:completed_intake',
        'conversation:changed_sub_status',
        'conversation:changed_status',
        'conversation:merged',
        'conversation:deleted',

        // Assignment changes
        'conversation:unassigned',
        'conversation:reassigned',
        'conversation:escalated',

        // Customer/Account changes
        'conversation:changed_customer',
        'conversation:changed_account',

        // Ticket type changes
        'conversation:changed_ticket_type',
        'conversation:changed_ticket_type_fields',
        'conversation:changed_priority',

        // Followers and collaborators
        'conversation:follower_user_added',
        'conversation:follower_team_added',
        'conversation:follower_user_removed',
        'conversation:follower_team_removed',
        'conversation:collaborator_user_added',
        'conversation:collaborator_team_added',
        'conversation:collaborator_user_removed',
        'conversation:collaborator_team_removed',

        // Triaging/workflow
        'conversation:triaged',
        'conversation:snoozed',

        // Tags
        'conversation:tags_added',
        'conversation:tags_removed',

        // Approvals
        'conversation:approval_request_created',
        'conversation:approval_request_approved',
        'conversation:approval_request_rejected',

        // External integrations
        'conversation:external_task_created',

        // Autoresponder
        'conversation:response_suggested',

        // Other actions
        'conversation:article_created',
        'conversation:widget_continuation_email_sent',

        // Escalation events
        'conversation:escalation_created',
        'conversation:escalation_acknowledged',
        'conversation:escalation_resolved',
        'conversation:escalation_updated',
      ],
      (data: any) => {
        // Only process events relevant to this conversation
        if (data?.data?.id === this.conversationId || data?.data?.conversationId === this.conversationId) {
          this.eventSubject.next(data);
        }
      },
    );

    if (this.conversationId) {
      // Try to pull from a local cache for faster load time
      const cachedConvo = this.conversationsService.cachedConversation;

      if (cachedConvo?.id === this.conversationId) {
        this.conversation = cachedConvo;
      }

      await this.loadConversation();
    }

    if (tenant?.accountStatus === 'trial' && this.authService.isTenantAdmin) {
      const end = moment(tenant.trialEndDate);
      this.disableButtons = end.diff(moment()) < 0;
    }

    const promises: Promise<any>[] = [
      this.fetchTimeline(),
      this.checkUserSlackScopes(),
      this.loadConnectedTeams(),
      this.emojisService.loadEmojis(),
      this.initializePriorityTokens(),
    ];

    Promise.all(promises);

    // Subscribe to CustomCommandBarService events for conversation actions
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.closeConversation$.subscribe(() => this.closeConversation()),
    );
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.reopenConversation$.subscribe(() => this.reopenConversation()),
    );
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.addTagsToConversation$.subscribe(() => this.addTag()),
    );
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.reassignConversation$.subscribe(() => this.showAssigneeOptions()),
    );
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.changePriority$.subscribe(() => this.showPriorityOptions()),
    );
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.openConversationInSlack$.subscribe(() => this.openConversationInSlack()),
    );
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.snoozeConversation$.subscribe(() => this.snooze()),
    );
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.composeReply$.subscribe(() => this.focusComposeField()),
    );
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.changeStatus$.subscribe(() => this.showSubStatusOptions()),
    );
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.addFollower$.subscribe(() => this.addFollower()),
    );
    this.commandBarActionsSubscription.add(
      this.customCommandBarService.searchKnowledgeBase$.subscribe(() => this.selectKnowledgeTab()),
    );
  }

  ngOnDestroy() {
    this.customCommandBarService.unregisterContext('conversationDetailsActive'); // Unregister context
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
    // Unsubscribe from CustomCommandBarService events
    this.commandBarActionsSubscription.unsubscribe();
  }

  async loadConnectedTeams() {
    this.connectedTeams = await this.slackService.getConnectedTeams();
  }

  async fetchTimeline(animated = true, isInitial = true) {
    try {
      // Set loading state to true at the beginning
      this.isLoadingTimeline = true;

      if (isInitial) {
        await this.loadConversation();
      }

      await Promise.all([this.loadMessages(isInitial), this.loadAuditLog(), this.loadLinkedConversations()]);

      const userSlackIds = new Set<string>();
      let shouldLoadApprovals = false;

      this.timelineEvents = this.messages
        .map((m) => {
          if (m.reactions?.length) {
            for (const reaction of m.reactions) {
              if (reaction.users?.length) {
                for (const user of reaction.users) {
                  userSlackIds.add(user);
                }
              }
            }
          }

          return {
            type: 'message',
            timestamp: new Date(m.timestamp),
            data: transformMessage(m),
          };
        })
        .concat(
          this.auditLog.map((a) => {
            if (a.type === 'approval_request_created') {
              shouldLoadApprovals = true;
            }

            return {
              type: 'auditItem',
              timestamp: new Date(a.timestamp),
              data: a,
            };
          }),
        )
        .concat(
          this.linkedConversations.map((link) => {
            const [linkedConversationId, unidirectionalTypeIndex] =
              link.linkedConversationId === this.conversationId
                ? [link.conversationId, 1]
                : [link.linkedConversationId, 0];

            const unidirectionalType = CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_MAP[link.type][unidirectionalTypeIndex];

            return {
              type: 'conversationLink',
              timestamp: new Date(link.createdAt),
              data: {
                icon: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[unidirectionalType],
                label: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[unidirectionalType],
                type: unidirectionalType,
                linkedConversationId,
              },
            };
          }),
        )
        .sort((a: any, b: any) => {
          // HACK: For bot dms, the initial message can come after the auto response in the list so we want to re-arrange them.
          if (a.type === 'message' && a.data.id === this.conversation.initialMessageId) {
            return -1;
          }

          if (b.type === 'message' && b.data.id === this.conversation.initialMessageId) {
            return 1;
          }

          if (a.timestamp < b.timestamp) {
            return -1;
          } else if (b.timestamp < a.timestamp) {
            return 1;
          }

          return 0;
        })
        // For bot dms, we want to filter out the triage style message that starts the main thread.
        .filter((e) => !(this.isBotDmBased && e.type === 'message' && e.data.id === this.conversation.initialMessageId))
        .filter(
          (e) =>
            !(
              e.type === 'message' &&
              e.data.subtype === 'file_share' &&
              (e.data.deletedAt || e.data.files?.length === 0)
            ),
        );

      const lookups: Record<string, any> = await this.apiService.postPromise('/saved-filters/get-lookup-values', {
        userSlackId: Array.from(userSlackIds),
      });

      this.lookups = {
        ...this.lookups,
        ...lookups,
      };

      this.setEmojiReactionTooltips();

      if (shouldLoadApprovals) {
        this.loadApprovalRequestData();
      }

      // For the first message in an email thread, expand it by default (otherwise takes up too much space)
      if (
        isInitial &&
        this.isEmailBased &&
        this.timelineEvents[0].type === 'message' &&
        !this.conversation.responseTime
      ) {
        this.timelineEvents[0].data.isExpanded = true;
        animated = false;
      }

      if (animated) {
        setTimeout(() => {
          this.content.scrollToBottom(animated ? 300 : 0);
        }, 50);
      }

      await Promise.all([this.loadTags()]);
    } finally {
      this.isLoadingTimeline = false;
    }
  }

  async loadAuditLog() {
    try {
      this.auditLog = await this.conversationsService.getAuditLog(this.conversationId, 'low');

      const lookups: Record<string, any> = await this.apiService.postPromise(
        '/saved-filters/get-lookup-values',
        this.auditLog.reduce(
          (acc, log) => {
            if (log.type === 'changed_sub_status') {
              acc.subStatusKey.push(log.data.subStatusKey);
              acc.subStatusKey.push(log.data.previousSubStatusKey);
            }

            return acc;
          },
          {
            subStatusKey: [],
          },
        ),
      );

      this.lookups = {
        ...this.lookups,
        ...lookups,
      };
    } catch (error) {
      console.error('Error loading audit log', error);
      this.auditLog = [];
    }
  }

  displayDefaultAuditLogType(type: string) {
    return type.replaceAll('_', ' ');
  }

  async loadConversation() {
    try {
      this.isLoadingConversation = true;
      this.conversation = await this.conversationsService.get(this.conversationId);
      this.conversation.resolvedTitle = getHtmlFromSlackMd(formatSlackContent(this.conversation.resolvedTitle)) as any;
      this.conversationId = this.conversation.id;
      this._calculateAllFieldDefinitions(); // Added call
    } catch (e) {
      this.isInvalidConversation = true;
      return;
    } finally {
      this.isLoadingConversation = false;
    }

    this.isEmailBased = Boolean(this.conversation.initialMessage?.sourceType === MESSAGE_SOURCE_TYPE.EMAIL);
    this.isWidgetBased = Boolean(this.conversation.initialMessage?.sourceType === MESSAGE_SOURCE_TYPE.WIDGET);
    this.isMicrosoftTeamsBased = Boolean(this.conversation.initialMessage?.sourceType === MESSAGE_SOURCE_TYPE.TEAMS);
    this.isDashboardBased = Boolean(this.conversation.initialMessage?.sourceType === MESSAGE_SOURCE_TYPE.DASHBOARD);
    this.isBotDmBased = Boolean(this.conversation.initialMessage?.sourceType === MESSAGE_SOURCE_TYPE.SLACK_BOT_DM);
    this.submittedBy = this.conversation.submitterUser;

    this.collaborators =
      this.conversation.collaborators?.reduce(
        (acc, c) => {
          acc[c.collaboratorType.id] = c;

          return acc;
        },
        {} as Record<string, ConversationCollaborator>,
      ) ?? {};

    if (this.conversation.externalTasks?.length) {
      this.openTaskAccordions = 'tasks';
    }

    if (this.conversation.escalations?.length) {
      this.getEscalations();
    }
  }

  async loadTags() {
    this.tags = await this.conversationsService.getTags(this.conversationId);
  }

  async loadLinkedConversations() {
    this.linkedConversations = await this.conversationsService.getConversationLinks(this.conversationId);
  }

  async loadMessages(isInitial = true) {
    this.uniqueAttachmentIds.clear();
    this.wasManuallySplit = false;

    const options: any = {
      limit: null,
      order: ['timestamp'],
      descending: false,
    };

    const [response, triageResponse] = await Promise.all([
      this.conversationsService.getMessages(this.conversationId, options),
      this.conversationsService.getTriageMessages(this.conversationId, options),
    ]);

    this.messages = [];

    // HACK: Filter out our bot message that starts the thread and other private explanation messages.
    for (const m of response.data) {
      if (m.manualSplitMessageId) {
        this.wasManuallySplit = true;
      }

      if (!m.isPrivateExplanation) {
        this.messages.push(m);
      }
    }

    this.messages = this.messages.concat(
      triageResponse.data.reduce((acc, m) => {
        const isInitialTriageMessage = m.id === m.triageThread.initialMessage.id;

        if (!isInitialTriageMessage) {
          acc.push(m);
        } else {
          const noteBlocks = (m.blocks ?? m.attachments?.[0]?.blocks) as any[];

          const plainNote = noteBlocks?.find((block: any) => block.block_id === 'note')?.text?.text ?? null;
          const noteTaggedUser =
            noteBlocks?.find((block: any) => block.block_id === 'note_tagged_user')?.text?.text ?? null;
          const noteMessage = noteBlocks?.find((block: any) => block.block_id === 'note_message')?.text?.text ?? null;
          const noteParts = [noteTaggedUser, noteMessage].filter((p) => p);
          const initialNote = plainNote ?? (noteParts?.length ? noteParts.join(' ') : null);

          if (initialNote) {
            m.text = formatSlackContent(m.resolvedContent);

            acc.push(m);
          }
        }

        return acc;
      }, []),
    );
    this.messages.sort((a, b) => new Date(a.timestamp).valueOf() - new Date(b.timestamp).valueOf());

    // Automatically set the compose box to a reply or internal note state based on last message
    // If last message was an internal message, then default the composer to sending an internal note
    if (isInitial && this.messages.length > 0) {
      const lastMessage = this.messages[this.messages.length - 1];
      const triageMessage = lastMessage?.triageThread?.initialMessage;

      if (triageMessage && triageMessage.slackChannel && lastMessage.id !== triageMessage.id) {
        this.composer.replyingToThreadId = triageMessage.ts;
        this.composer.replyingToThreadLabel = `in #${triageMessage.slackChannel.name}`;
      } else if (this.isDashboardBased) {
        const conversationTriageMessage = this.conversation.triageMessages?.[0];

        if (conversationTriageMessage) {
          this.composer.replyingToThreadId = conversationTriageMessage.ts;
          this.composer.replyingToThreadLabel = `in #${conversationTriageMessage.channelName}`;
        }
      } else if ((this.isEmailBased || this.isWidgetBased || this.isMicrosoftTeamsBased) && lastMessage.isPrivateNote) {
        this.composer.replyingToThreadId = this.conversation.initialMessage.ts;
        this.composer.replyingToThreadLabel = undefined;
      }
    }

    // Kind of a hack: Keep track of the email we're responding to and display in reply-composer
    let latestInboundEmail: InboundEmail;

    this.messages = this.messages.flatMap((message) => {
      let result = [message];
      const isInbound = Boolean(message.inbound);

      if (isInbound && message.inbound.inboundEmail) {
        latestInboundEmail = message.inbound.inboundEmail;
      }

      let resolvedContent = getHtmlFromSlackMd(_.unescape(message.text) ?? '') as any;

      if (
        (this.isEmailBased || this.isWidgetBased || this.isMicrosoftTeamsBased || this.isBotDmBased) &&
        (!isInbound || message.emailId)
      ) {
        resolvedContent = resolvedContent?.replace(/\/unthread(?: .*)?<\/p>$/, '</p>') ?? '';
        message.htmlContent = message.htmlContent?.replace(/\/unthread(?: .*)?<\/p>/, '</p>') ?? '';

        if (resolvedContent.endsWith('<br/><br/></p>')) {
          // Hacky workaround to remove trailing newline characters at the end of messages
          resolvedContent = `${resolvedContent.substr(0, resolvedContent.length - 14)}</p>`;
        }
      }

      message.resolvedContent = resolvedContent;

      // HACK: Detect if there was a survey sent in this message so we can render it properly.
      const surveyBlock = message.blocks?.find(
        (b) =>
          b.elements?.[0]?.action_id === 'open_survey_modal' ||
          b.elements?.[0]?.action_id?.startsWith('inline_survey_'),
      );

      if (surveyBlock) {
        const isButton = surveyBlock.elements[0].action_id === 'open_survey_modal';
        const isNps = isButton ? null : surveyBlock.elements.length > 5;

        message.survey = {
          buttonLayout: isButton ? 'single' : 'inline',
          type: isNps ? 'nps' : 'csat',
          buttonLabel: isButton ? surveyBlock.elements[0].text.text : null,
        };

        if (isButton) {
          message.resolvedContent +=
            `<button class="btn-small" fill="outline" color="dark">${message.survey.buttonLabel}</button>` as any;
        } else if (isNps) {
          message.resolvedContent += `1️⃣ 2️⃣ 3️⃣ 4️⃣ 5️⃣ 6️⃣ 7️⃣ 8️⃣ 9️⃣ 🔟` as any;
        } else if (surveyBlock.elements[0].text.text === ':star-struck:') {
          message.resolvedContent += `🤩 🙂 😐 😞 😡` as any;
        } else {
          message.resolvedContent += `1️⃣ 2️⃣ 3️⃣ 4️⃣ 5️⃣` as any;
        }
      }

      // HACK: When posting an attachment, we don't want the attachment to show up in both the original message and as its own message.
      for (const file of message.files ?? []) {
        if (this.uniqueAttachmentIds.has(file.id)) {
          // remove the file from the list if it's a duplicate
          message.files = message.files.filter((f) => f.id !== file.id);
        }

        this.uniqueAttachmentIds.add(file.id);
      }

      // HACK: Detect if there were inline images sent in this message so we can render them properly.
      const imageBlocks = message.blocks?.filter((b) => b.type === 'image');

      if (imageBlocks?.length && !message.deletedAt) {
        message.resolvedContent += `<div class="conversation-message-inline-images-container">` as any;

        for (const imageBlock of imageBlocks) {
          // If this is a slack file, add it to the files array
          // e.g. https://files.slack.com/files-pri/T0411NL9H96-F070RCVGNBH/image.png
          if (imageBlock.image_url?.startsWith('https://files.slack.com')) {
            const path = new URL(imageBlock.image_url).pathname;
            const fileId = path.split('/')?.[2]?.split('-')?.[1];

            message.files = (message.files || []).concat({
              url: imageBlock.image_url,
              name: imageBlock.alt_text || imageBlock.fallback,
              id: fileId,
            });
          } else {
            message.resolvedContent += `<img src="${imageBlock.image_url}" alt="${
              imageBlock.alt_text || imageBlock.fallback
            }" class="block-element" />` as any;
          }
        }

        message.resolvedContent += `</div>` as any;
      }

      const botDmSlackAttachments = message.blocks
        ?.filter((b) => b.block_id?.startsWith('bot_dm_attachments_'))
        .flatMap((attachmentsBlock: any) => {
          return attachmentsBlock.elements
            .map((attachment: any) => {
              try {
                const [fileLink, fileName] = attachment.text.split('|');
                const slackFileId = new URL(fileLink.replace('• <', '')).searchParams.get('downloadFileId');

                return { slackId: slackFileId, name: fileName.replace('>', '') };
              } catch (e) {
                return null;
              }
            })
            .filter((slackAttacment) => slackAttacment);
        });

      if (botDmSlackAttachments?.length) {
        if (!this.slackAttachments) {
          this.slackAttachments = {};
        }

        this.slackAttachments[message.id] = botDmSlackAttachments;
      }

      const pendingMessage = this.pendingMessages?.[message.ts];

      if (pendingMessage) {
        if (pendingMessage.attachments?.length && !message.files?.length) {
          result = [];
        } else {
          this.pendingMessages = _.omit(this.pendingMessages, message.ts);
        }
      }
      return result;
    });

    if (latestInboundEmail && !this.tos?.length) {
      const toUser = this.conversation.submitterUser?.email
        ? this.conversation.submitterUser
        : this.conversation.initialMessage?.inbound?.inboundEmail?.fromUser;
      let to: Recipient | undefined = toUser
        ? { name: toUser.name ?? '', email: toUser.email, editable: false }
        : undefined;
      const existingCcs = latestInboundEmail.ccs ?? [];
      const existingTos = latestInboundEmail.tos ?? [];

      const ccs = existingCcs
        .concat(existingTos)
        .filter(
          (entry) =>
            !isDoNotReplyEmail(entry.email) &&
            entry.email !== to?.email &&
            entry.email !== this.conversation.inboundEmailRule?.email,
        )
        .map((entry) => {
          return { email: entry.email, name: entry.name, editable: true };
        });

      const latestInboundEmailFromUser = latestInboundEmail.fromUser;

      if (
        latestInboundEmailFromUser?.email &&
        to?.email !== latestInboundEmailFromUser.email &&
        !ccs.find((cc) => cc.email === latestInboundEmailFromUser.email)
      ) {
        ccs.push({
          email: latestInboundEmailFromUser?.email,
          name: latestInboundEmailFromUser?.name,
          editable: true,
        });
      }

      while (true) {
        if ((!to?.email || isDoNotReplyEmail(to.email)) && this.ccs.length) {
          to = this.ccs.shift()!;
        } else {
          break;
        }
      }

      this.tos = [to];

      if (this.composer) {
        this.composer.setEmailRecipients({ tos: this.tos, ccs, bccs: [] });
      }
    }

    const initialMessage = this.messages[0];
    this.referralUrl = initialMessage?.inbound?.widgetMessage?.referrerUrl;
  }

  async loadTasks() {
    this.isLoadingTasks = true;
    await this.loadConversation();
    this.isLoadingTasks = false;
  }

  async loadCollaboratorTypes() {
    this.collaboratorTypes = await this.collaboratorTypesService.getCachedCollaboratorTypes();
  }

  viewUser(user: any) {
    this.navController.navigateForward(formatLink(user.id, user.isExternal));
  }

  getSlackLinkForConversationMessage(messageTs?: string) {
    const threadTs = this.conversation.threadTs;
    const channelId = this.conversation.channelId;

    return this.getSlackLink(
      messageTs,
      threadTs,
      channelId,
      this.conversation.slackChannel?.teamId ?? this.conversation.teamId,
    );
  }

  getSlackLink(messageTs?: string, threadTs?: string, channelId?: string, teamId?: string) {
    return getSlackLink(
      teamId ?? this.conversation.teamId,
      messageTs,
      threadTs,
      channelId,
      this.conversation.teamId === this.authService.tenant.slackTeamId
        ? this.authService.tenant.slackTeamUrl
        : undefined,
    );
  }

  openConversationInSlack() {
    this.analyticsService.trackEvent('Conversation Details', 'Open Conversation in Slack');

    window.open(
      this.getSlackLinkForConversationMessage(
        this.messages?.length ? this.messages[this.messages.length - 1]?.ts : undefined,
      ),
    );
  }

  openMessageInSlack(messageTs: string) {
    this.analyticsService.trackEvent('Conversation Details', 'Open Message in Slack');

    window.open(this.getSlackLinkForConversationMessage(messageTs));
  }

  openTriageInSlack(messageTs: string, channelId: string) {
    this.analyticsService.trackEvent('Conversation Details', 'Open Triage Message');

    window.open(this.getSlackLink(messageTs, null, channelId));
  }

  // multiple approval requests with multiple popovers might cause focus
  // related issues. for more, see:
  // https://github.com/ionic-team/ionic-framework/issues/22795
  async showApprovalRequestItemActions(approvalRequest: ApprovalRequest) {
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options: [
          {
            label: 'Cancel and unsend approval request',
            icon: 'close-circle-outline',
            destructive: true,
          },
        ],
        callback: (idx: number) => {
          popover.dismiss();
          if (idx === 0) {
            this.deleteApprovalRequest(approvalRequest);
          }
        },
      },
    });
    popover.present();
  }

  async deleteApprovalRequest(approvalRequest: ApprovalRequest) {
    try {
      await this.conversationsService.deleteApprovalRequest(this.conversationId, approvalRequest.id);
    } catch (err) {
      this.msgSrvc.showError(err);
    }

    await this.loadApprovalRequestData();
  }

  async showConversationOptions() {
    const LABEL_COPY_LINK = 'Copy link';
    const LABEL_EXCLUDE_ANALYTICS = this.conversation?.excludeAnalytics
      ? 'Include in analytics'
      : 'Exclude from analytics';
    const LABEL_CHANGE_REOPENING = this.conversation?.lockedAt
      ? 'Allow automatic reopening'
      : 'Prevent automatic reopening';
    const LABEL_MERGE = 'Merge conversation into...';
    const LABEL_ADD_FOLLOWERS = 'Add followers...';
    const LABEL_CHANGE_CUSTOMER = 'Change linked customer...';
    const LABEL_CHANGE_ACCOUNT = 'Change linked account...';
    const LABEL_BLOCK = 'Block...';
    const LABEL_DELETE = 'Delete conversation...';
    const LABEL_GENERATE = 'Create knowledge base article...';

    const options: ActionDropdownType[] = [
      {
        label: LABEL_COPY_LINK,
        icon: 'link-outline',
      },
      {
        label: LABEL_EXCLUDE_ANALYTICS,
        icon: this.conversation?.excludeAnalytics ? 'cloud-done-outline' : 'cloud-offline-outline',
      },
      {
        label: LABEL_MERGE,
        icon: 'git-merge-outline',
      },
      {
        label: LABEL_ADD_FOLLOWERS,
        icon: 'person-add-outline',
      },
      this.authService.tenant.accountsEnabled
        ? {
            label: LABEL_CHANGE_ACCOUNT,
            icon: 'business-outline',
          }
        : {
            label: LABEL_CHANGE_CUSTOMER,
            icon: 'business-outline',
          },
      {
        label: LABEL_CHANGE_REOPENING,
        icon: this.conversation?.lockedAt ? 'lock-open-outline' : 'lock-closed-outline',
      },
    ];

    if (this.conversation?.status === 'closed' && !this.conversation?.knowledgeBaseArticle) {
      options.push({
        label: LABEL_GENERATE,
        icon: 'sparkles-outline',
      });
    }

    if (this.conversation?.inboundEmailRuleId && this.submittedBy.email) {
      options.push({
        label: LABEL_BLOCK,
        icon: 'ban-outline',
      });
    }

    options.push({
      label: LABEL_DELETE,
      icon: 'trash-outline',
      destructive: true,
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: (_idx: number, label: string) => {
          popover.dismiss();

          if (label === LABEL_COPY_LINK) {
            this.copyText(window.location.href);
          } else if (label === LABEL_EXCLUDE_ANALYTICS) {
            this.toggleAnalytics();
          } else if (label === LABEL_MERGE) {
            this.mergeConversation(event);
          } else if (label === LABEL_ADD_FOLLOWERS) {
            this.addFollower();
          } else if (label === LABEL_CHANGE_CUSTOMER) {
            this.changeCustomer(event);
          } else if (label === LABEL_CHANGE_REOPENING) {
            this.toggleLocked();
          } else if (label === LABEL_DELETE) {
            this.deleteConversation();
          } else if (label === LABEL_BLOCK) {
            this.showBlockOptions();
          } else if (label === LABEL_GENERATE) {
            this.getArticleSuggestion();
          } else if (label === LABEL_CHANGE_ACCOUNT) {
            this.changeAccount(event);
          }
        },
      },
    });
    popover.present();
  }

  async getArticleSuggestion() {
    // Navigate to the new article page, passing conversationId as query param
    this.router.navigate(['/dashboard/knowledge/articles/new'], {
      queryParams: { conversationId: this.conversationId },
    });
  }

  async copyText(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.msgSrvc.show('✓ Copied to clipboard');
      })
      .catch((err) => {
        console.error('Could not copy text', err);
      });
  }

  async mergeConversation(event) {
    this.searchablePopoverService.conversations({
      event,
      customerId: this.conversation.customerId,
      projectId: this.conversation.projectId,
      where: [
        {
          field: 'id',
          operator: '!=',
          value: this.conversationId,
        },
      ],
      callback: async (id: string, _name: string, conversation: Partial<Conversation>) => {
        if (id) {
          const alert = await this.alertCtrl.create({
            header: `Confirm merge into #${conversation.friendlyId}`,
            subHeader: 'This cannot be undone',
            buttons: [
              {
                text: 'Confirm',
                handler: async () => {
                  const loader = await this.loadingCtrl.create({
                    message: 'Merging...',
                    spinner: 'crescent',
                  });
                  loader.present();

                  try {
                    await this.conversationsService.merge(this.conversationId, id);
                    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/all/conversations/${id}`);
                    loader.dismiss();
                  } catch (e) {
                    loader.dismiss();
                    this.msgSrvc.show('Failed to merge, please try again');
                    console.error(e);
                  }
                },
              },
              {
                text: 'Cancel',
              },
            ],
          });
          alert.present();
        }
      },
    });
  }

  async changeCustomer(event) {
    this.searchablePopoverService.customers({
      event,
      selectedCustomerId: this.conversation.customerId,
      projectId: this.conversation.projectId,
      callback: async (id: string) => {
        if (id) {
          const loader = await this.loadingCtrl.create({
            message: 'Updating...',
            spinner: 'crescent',
          });
          loader.present();

          try {
            this.conversation = await this.conversationsService.update(this.conversationId, {
              customerId: id,
            });

            loader.dismiss();
          } catch (e) {
            loader.dismiss();
            this.msgSrvc.show('Failed to update, please try again');
            console.error(e);
          }
        }
      },
    });
  }

  async changeAccount(event) {
    this.searchablePopoverService.accounts({
      event,
      selectedAccountId: this.conversation.accountId,
      callback: async (id: string) => {
        if (id) {
          const loader = await this.loadingCtrl.create({
            message: 'Updating...',
            spinner: 'crescent',
          });
          loader.present();

          try {
            this.conversation = await this.conversationsService.update(this.conversationId, {
              accountId: id === this.conversation.accountId ? null : id,
            });

            loader.dismiss();
          } catch (e) {
            loader.dismiss();
            this.msgSrvc.show('Failed to update, please try again');
            console.error(e);
          }
        }
      },
    });
  }

  async showBlockOptions() {
    const buttons = [
      {
        text: 'Block user',
        handler: async () => {
          this.block(this.submittedBy.email);
        },
      },
    ];

    const domain = this.submittedBy.email.split('@')[1];
    if (!CONSUMER_EMAIL_DOMAINS.includes(domain.toLowerCase())) {
      buttons.push({
        text: 'Block all emails from this domain',
        handler: async () => {
          this.block(getEmailDomain(this.submittedBy.email));
        },
      });
    }

    const header = `Block ${this.conversation.submitterUser.email}?`;
    const subHeader = 'This will prevent new emails from being received, and this conversation will be closed.';
    const alert = await this.alertCtrl.create({
      header,
      subHeader,
      buttons: [
        ...buttons,
        {
          text: 'Cancel',
        },
      ],
    });
    alert.present();
  }

  async block(emailOrDomain: string) {
    const loader = await this.loadingCtrl.create({
      message: 'Blocking...',
      spinner: 'crescent',
    });

    loader.present();

    try {
      const rule = await this.inboundEmailRulesService.getById(this.conversation.inboundEmailRuleId);

      const blocklist = rule.blocklist || [];
      blocklist.push(emailOrDomain);

      await this.inboundEmailRulesService.updatePartial(rule.id, { blocklist });
      await this.conversationsService.update(this.conversation.id, {
        status: CONVERSATION_STATUS.CLOSED,
        excludeAnalytics: true,
      });

      loader.dismiss();
      this.dismiss();
    } catch (e) {
      loader.dismiss();

      const header = `There was an issue blocking this user`;
      const subHeader = 'You may not have the right permissions.';
      const alert = await this.alertCtrl.create({
        header,
        subHeader,
        buttons: [
          {
            text: 'Dismiss',
          },
        ],
      });
      alert.present();
    }
  }

  async deleteConversation() {
    if (!this.authService.isTenantAdmin) {
      const alert = await this.alertCtrl.create({
        header: 'You must be an admin to delete conversations',
        buttons: [
          {
            text: 'Dismiss',
          },
        ],
      });
      alert.present();
      return;
    }

    const subHeader = 'This cannot be undone';
    const alert = await this.alertCtrl.create({
      header: 'Are you sure you want to delete this conversation?',
      subHeader,
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Confirm',
          handler: async () => {
            const loader = await this.loadingCtrl.create({
              message: 'Deleting...',
              spinner: 'crescent',
            });
            loader.present();

            try {
              await this.conversationsService.delete(this.conversationId);
              this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/all`);
              this.msgSrvc.show('Conversation has been deleted');
              loader.dismiss();
            } catch (e) {
              loader.dismiss();
              this.msgSrvc.show('Failed to delete, please try again');
              console.error(e);
            }
          },
        },
      ],
    });
    alert.present();
  }

  getMessageOptions(message: ConversationDetailsMessage) {
    const { userType, botId, inbound, emailId, widgetMessageId, microsoftTeamsMessageId } = message;

    const options: ActionDropdownType[] = [];

    if (this.isViewAgentThoughtsEnabled() && userType === 'bot' && !message.aiThoughts) {
      options.push({
        label: labelViewAgentThoughts,
        icon: 'bulb-outline',
      });
    }

    if (userType === 'bot' || userType === 'customer' || userType === 'internal_customer') {
      options.push({
        label: labelAskKb,
        icon: 'sparkles-outline',
      });
    }

    if (userType === 'bot' || userType === 'internal') {
      options.push({
        label: labelMarkAsResolvingComment,
        icon: 'checkmark-circle-outline',
      });
      options.push({
        label: labelAddToKb,
        icon: 'book-outline',
      });
    }

    if (
      !inbound &&
      userType === 'internal' &&
      message.sourceType === MESSAGE_SOURCE_TYPE.SLACK &&
      message.blocks?.length
    ) {
      options.push({
        label: labelAddAsTemplate,
        icon: 'shapes-outline',
      });
    }

    if (
      !this.getIsPrivateMessage(message) &&
      this.conversation.initialMessageId !== message.id &&
      !emailId &&
      !widgetMessageId &&
      !microsoftTeamsMessageId
    ) {
      options.push({
        label: labelSplitConversation,
        icon: 'git-network-outline',
      });
    }

    if (
      (!emailId &&
        !widgetMessageId &&
        !microsoftTeamsMessageId &&
        this.authService.tenant.slackBotBotId &&
        this.authService.tenant.slackBotBotId === botId) ||
      (message.user?.id == this.authService.userId && this.hasUserChatWriteScope)
    ) {
      options.push({
        label: labelDeleteMessage,
        icon: 'trash-outline',
        destructive: true,
      });
    }

    return options;
  }

  getObjectEntries(obj: any) {
    return Object.entries(obj).map(([key, value]) => ({ key, value }));
  }

  viewAgentThoughts(message: ConversationDetailsMessage) {
    this.aiTesterService.getAgentThoughts(message.id).then((thoughts) => {
      message.aiThoughts = thoughts;
      message.aiThoughtsShowLowConfidence = thoughts
        ? thoughts.some((thought) => {
            return (
              thought.aspects &&
              ((thought.aspects.topic_selection_confidence != undefined &&
                thought.aspects.topic_selection_confidence < AGENT_CONFIDENCE_THRESHOLD) ||
                (thought.aspects.action_to_take_confidence != undefined &&
                  thought.aspects.action_to_take_confidence < AGENT_CONFIDENCE_THRESHOLD))
            );
          })
        : undefined;
    });
  }

  async showMessageOptions(message: ConversationDetailsMessage) {
    const options = this.getMessageOptions(message);

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: (_idx: number, label: string) => {
          popover.dismiss();

          if (label === labelDeleteMessage) {
            this.deleteMessage(message.id);
          } else if (label === labelMarkAsResolvingComment) {
            this.markAsResolvingComment(message);
          } else if (label === labelAddAsTemplate) {
            this.createTemplateFromMessage(message);
          } else if (label === labelAskKb) {
            this.askKb(message);
          } else if (label === labelViewAgentThoughts) {
            this.viewAgentThoughts(message);
          } else if (label === labelSplitConversation) {
            this.splitConversation(message);
          } else if (label === labelAddToKb) {
            this.addToKb(message);
          }
        },
      },
    });
    popover.present();
  }

  async splitConversation(message: ConversationDetailsMessage) {
    const confirm = await this.alertCtrl.create({
      header: `Split Conversation?`,
      subHeader:
        'This will start a new conversation with this message, and the previous conversation can no longer be responded to.',
      buttons: [
        {
          text: 'Cancel',
          handler: () => {},
        },
        {
          text: 'Split',
          handler: async (data: any[]) => {
            this.isLoadingTimeline = true;

            const loader = await this.loadingCtrl.create({
              message: 'Creating new conversation...',
              spinner: 'crescent',
            });

            loader.present();

            try {
              const result = (await this.conversationsService.splitConversation(
                this.conversation.id,
                message.id,
              )) as any;

              if (result.conversationId) {
                this.navController.navigateRoot(
                  `${routes.DASHBOARD}/${routes.INBOX}/all/conversations/${result.conversationId}`,
                );
              }
            } catch (err) {
              this.msgSrvc.showError(err);
            }

            loader.dismiss();

            this.isLoadingTimeline = false;
          },
        },
      ],
    });

    confirm.present();
  }

  async createTemplateFromMessage(message: ConversationDetailsMessage) {
    const confirm = await this.alertCtrl.create({
      header: `Create Template`,
      subHeader: 'Add a name to easily use this message content in future replies',
      inputs: [
        {
          placeholder: 'Enter template name',
          attributes: {
            maxLength: 320,
          },
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          handler: () => {},
        },
        {
          text: 'Save',
          handler: async (data: any[]) => {
            let name = data[0];

            if (!name) {
              return;
            }

            const loader = await this.loadingCtrl.create({
              message: 'Saving...',
              spinner: 'crescent',
            });

            loader.present();

            try {
              await this.apiService.postPromise(`/templates`, {
                name: name,
                type: TEMPLATE_TYPE.SLACK,
                isDiscoverable: true,
                chunks: message.blocks,
                design: null,
                html: null,
                communityId: null,
              });
              loader.dismiss();

              const confirm = await this.alertCtrl.create({
                header: `✔ Template saved`,
                buttons: [
                  {
                    text: 'Dismiss',
                    handler: () => {},
                  },
                  {
                    text: 'View templates',
                    handler: () => {
                      this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.OUTBOUND}/templates`);
                    },
                  },
                ],
              });
              confirm.present();
            } catch (err) {
              this.msgSrvc.showError(err);
              loader.dismiss();
            }
          },
        },
      ],
    });

    confirm.present();
  }

  async askKb(message: ConversationDetailsMessage) {
    this.sidebarTab = 'knowledge';
    setTimeout(() => {
      this.kbSearch.askAi(message.text);
    }, 300);
  }

  async addToKb(message: ConversationDetailsMessage) {
    // Data to pass to the new article page (via state or query params)
    // Using query params for simplicity here, could use router state for more complex data
    const queryParams: any = {
      prefillTitle: this.conversation.title ?? this.conversation.initialMessage?.text,
      prefillContent: message.text,
      // Add conversationId if needed for tracking or other purposes
      // conversationId: this.conversationId,
    };

    // Navigate to the new article page
    this.router.navigate(['/dashboard/knowledge/articles/new'], { queryParams });
  }

  selectKnowledgeTab(article?: string | Partial<KnowledgeBaseArticle>) {
    this.sidebarTab = 'knowledge';
    setTimeout(() => {
      if (article) {
        this.kbSearch.selectArticle(article);
      } else {
        this.kbSearch.focusInput();
      }
    }, 300);
  }

  async deleteMessage(messageId: string) {
    this.analyticsService.trackEvent('Conversation Details', 'Delete Reply');

    this.deletingMessageIds.add(messageId);

    try {
      await this.conversationsService.deleteMessage(this.conversationId, messageId);

      this.fetchTimeline(false, false);
    } catch (e) {
      this.msgSrvc.showError(e);

      this.deletingMessageIds.delete(messageId);
    }
  }

  markAsResolvingComment(message: ConversationDetailsMessage) {
    this.updateResolutionTime(
      (new Date(message.timestamp).valueOf() - new Date(this.conversation.createdAt).valueOf()) / 1000,
    );
  }

  async updateResolutionTime(resolutionTimeOverride: number) {
    if (resolutionTimeOverride < 0) {
      resolutionTimeOverride = 0;
    }

    this.conversation = await this.conversationsService.update(this.conversationId, {
      resolutionTimeOverride,
    });
  }

  async snooze(deferUpdate?: boolean): Promise<Date> {
    this.analyticsService.trackEvent('Conversation Details', 'Snooze');

    // Prevent keyboard shortcut from popping up multiple instances
    if (this.isSnoozeOpen) {
      return;
    }

    this.isSnoozeOpen = true;
    const popover = await this.popoverCtrl.create({
      component: SnoozeComponent,
      showBackdrop: false,
    });

    popover.present();

    const { data } = await popover.onWillDismiss();
    this.isSnoozeOpen = false;

    if (data?.snoozeDate && !deferUpdate) {
      await this.updateConversation({
        status: CONVERSATION_STATUS.ON_HOLD,
        snoozedAt: new Date(),
        wakeUpAt: data.snoozeDate.toDate(),
      });
      this.dismiss();
    }

    return data?.snoozeDate;
  }

  async updateConversation(changes: Partial<Conversation>, showSuccess = false) {
    this.isLoadingConversation = true;

    try {
      this.conversation = await this.conversationsService.update(this.conversationId, changes);

      if (showSuccess) {
        this.msgSrvc.show('✔︎ Saved', { duration: 2000, position: 'bottom' });
      }
    } catch (error) {
      this.msgSrvc.showError(error);
    } finally {
      this.isLoadingConversation = false;
    }
  }

  async closeConversation() {
    this.analyticsService.trackEvent('Conversation Details', 'Close Conversation');

    await this.updateConversation({ status: CONVERSATION_STATUS.CLOSED });
    this.dismiss();
  }

  async reopenConversation() {
    this.analyticsService.trackEvent('Conversation Details', 'Reopen Conversation');

    this.updateConversation({ status: CONVERSATION_STATUS.IN_PROGRESS });
  }

  dismiss() {
    const currentPath = window.location.pathname;
    this.navController.pop();

    // If nav controller can't pop, we still want to send them back to the inbox
    // Nav controller doesn't seem to have a way to easily detect whether we _can_ pop
    // So we're just going to hackily check whether we did pop.
    setTimeout(() => {
      if (currentPath === window.location.pathname) {
        this.navController.navigateRoot(
          currentPath.split(`/conversations/`)[0] ?? `${routes.DASHBOARD}/${routes.INBOX}/me`,
        );
      }
    }, 300);
  }

  getIsPrivateMessage(message: ConversationDetailsMessage) {
    if (this.authService.tenant.defaultToPublicMessages) {
      if (message.isPrivateNote || message.triageThread) {
        return true;
      } else {
        return false;
      }
    }

    if (message.emailId || message.widgetMessageId || message.microsoftTeamsMessageId) {
      return false;
    }

    return (
      ((this.isEmailBased && !message.inbound) ||
        (this.isWidgetBased && !message.inbound) ||
        (this.isMicrosoftTeamsBased && !message.inbound) ||
        (this.isBotDmBased &&
          message.sourceType !== MESSAGE_SOURCE_TYPE.SLACK_BOT_DM &&
          !message.channelId.startsWith('D') &&
          !message.reactions?.some((r: any) =>
            r.users?.some((u: string) => u === this.authService.tenant.slackBotId),
          )) ||
        message.triageThread) &&
      !this.sendingMessageIds.has(message.ts)
    );
  }

  displaySubtype(subtype: string) {
    if (subtype === 'file_share') {
      return 'File share';
    }

    return subtype;
  }

  displayStatus(status: string) {
    return status?.split('_').join(' ');
  }

  displaySubStatus(subStatusKey: string) {
    if (this.lookups.subStatusKey?.[subStatusKey]) {
      return this.lookups.subStatusKey[subStatusKey];
    }

    const [_type, ...parts] = subStatusKey.split('_');

    return parts.join(' ');
  }

  displayDuration(durationSecs: number) {
    return getDisplayTextForDuration(durationSecs);
  }

  displayPriority() {
    return this.priorityValueToTokenMap[this.conversation?.priority]?.LABEL || 'None';
  }

  displayPriorityEmoji(priority: number) {
    return priority ? this.priorityValueToTokenMap[priority]?.EMOJI || this.getIconForPriority(priority) : null;
  }

  displaySnooze() {
    return `💤 Snoozed – reopens ${moment(this.conversation.wakeUpAt).fromNow()}`;
  }

  displayDateFrom(date: Date) {
    return moment(date).fromNow();
  }

  getIconForPriority(priority: number) {
    return getIconForConversationPriority(priority);
  }

  getColorForPriority(priority: number) {
    return getColorForConversationPriority(priority);
  }

  async showStatusOptions(event?: Event) {
    this.searchablePopoverService.statuses({
      event,
      selectedStatusId: this.conversation.status,
      callback: (id: string) => {
        this.analyticsService.trackEvent('Conversation Details', 'Change Status');

        this.updateConversation({ status: id });
      },
    });
  }

  async showSubStatusOptions(event?: Event) {
    this.searchablePopoverService.subStatuses({
      event,
      selectedSubStatusId: this.conversation.subStatusId,
      // type: this.conversation.status,
      projectId: this.conversation.projectId,
      callback: (id: string) => {
        this.analyticsService.trackEvent('Conversation Details', 'Change Sub Status');

        this.updateConversation({ subStatusId: id });
      },
    });
  }

  async showAssigneeOptions(event?: Event) {
    this.searchablePopoverService.users({
      event,
      selectedUserId: this.conversation.assignedToUserId,
      dedupeInternalUsers: true,
      showAssignToMeText: this.conversation.assignedToUserId !== this.authService.userId ? 'Assign to me' : undefined,
      callback: (id: string) => {
        this.analyticsService.trackEvent('Conversation Details', 'Change Assignee');

        const updatedId = id === this.conversation.assignedToUserId ? null : id;
        this.updateConversation({ assignedToUserId: updatedId });
      },
    });
  }

  async showTicketTypeOptions() {
    this.searchablePopoverService.ticketTypes({
      event,
      selectedTicketTypeId: this.conversation.ticketType?.id,
      projectId: this.conversation.projectId,
      callback: (id: string) => {
        this.analyticsService.trackEvent('Conversation Details', 'Change Ticket Type');

        const updatedId = id === this.conversation.ticketType?.id ? null : id;
        this.updateConversation({ ticketTypeId: updatedId }, true);
      },
    });
  }

  async showPriorityOptions(event?: Event) {
    const options: ActionDropdownType[] = [
      {
        label: this.priorityTokens.LOW.LABEL,
        emoji: this.priorityTokens.LOW.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.LOW),
        description: this.priorityTokens.LOW.DESCRIPTION,
      },
      {
        label: this.priorityTokens.MEDIUM.LABEL,
        emoji: this.priorityTokens.MEDIUM.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.MEDIUM),
        description: this.priorityTokens.MEDIUM.DESCRIPTION,
      },
      {
        label: this.priorityTokens.HIGH.LABEL,
        emoji: this.priorityTokens.HIGH.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.HIGH),
        description: this.priorityTokens.HIGH.DESCRIPTION,
      },
      {
        label: this.priorityTokens.CRITICAL.LABEL,
        emoji: this.priorityTokens.CRITICAL.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.CRITICAL),
        description: this.priorityTokens.CRITICAL.DESCRIPTION,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();

          let priority: number | null;

          switch (idx) {
            case 0:
              priority = CONVERSATION_PRIORITY_VALUES.LOW;
              break;
            case 1:
              priority = CONVERSATION_PRIORITY_VALUES.MEDIUM;
              break;
            case 2:
              priority = CONVERSATION_PRIORITY_VALUES.HIGH;
              break;
            case 3:
              priority = CONVERSATION_PRIORITY_VALUES.CRITICAL;
              break;
          }
          this.analyticsService.trackEvent('Conversation Details', 'Change Priority');

          if (priority === this.conversation.priority) {
            priority = null;
          }

          this.updateConversation({ priority });
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  didBlurNotes() {
    this.updateConversation({ notes: this.conversation.notes }, true);
  }

  async editTitle() {
    const popover = await this.popoverCtrl.create({
      component: TextPopoverComponent,
      componentProps: {
        text: this.conversation.title,
        title: 'Edit title (may be visible to the customer)',
        placeholder: 'Enter title',
        shouldSelectAll: true,
        shouldSelect: true,
        showSaveButton: true,
        saveOnEnter: true,
        callback: async (title: string) => {
          if (title) {
            const loader = await this.loadingCtrl.create({
              message: 'Updating...',
              spinner: 'crescent',
            });
            loader.present();

            await this.updateConversation({ title }, false);

            loader.dismiss();
          }
        },
      },
      cssClass: 'wide-popover',
      showBackdrop: true,
      event,
    });
    popover.present();
  }

  async editResolutionTime() {
    const confirm = await this.alertCtrl.create({
      header: `Edit Resolution Time (minutes)`,
      cssClass: 'wide-alert-controller',
      inputs: [
        {
          placeholder: 'Enter resolution time here',
          value: (this.conversation.resolutionTimeWorking / 60).toFixed(2),
          attributes: {
            type: 'number',
          },
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          handler: () => {},
        },
        {
          text: 'Save',
          handler: async (data: any[]) => {
            const resolutionTime = data[0];

            if (!resolutionTime) {
              return;
            }

            const loader = await this.loadingCtrl.create({
              message: 'Updating...',
              spinner: 'crescent',
            });

            loader.present();

            await this.updateResolutionTime(Number(resolutionTime) * 60);

            loader.dismiss();
          },
        },
      ],
    });

    confirm.present();
  }

  async triage() {
    const modal = await this.modalCtrl.create({
      component: TriageComponent,
      cssClass: 'modal-content-only modal-sm',
      componentProps: {
        conversationId: this.conversationId,
        shouldLoadDefaultTriageChannel: !this.filteredTriageMessages()?.length,
        customerId: this.conversation.customerId,
      },
    });

    modal.present();

    modal.onDidDismiss().then(async ({ data }: any) => {
      if (data?.message) {
        this.analyticsService.trackEvent('Conversation Details', 'Manually Triage');
      }
    });
  }

  async addTag(event?: Event) {
    this.searchablePopoverService.tags({
      event,
      projectId: this.conversation.projectId,
      callback: async (id: string) => {
        this.analyticsService.trackEvent('Conversation Details', 'Add Tag');

        await this.tagsService.createConversationLinks(id, [this.conversation.id]);

        await this.loadTags();
      },
    });
  }

  async showTagOption(tag: any) {
    const LABEL_APPROVE_AUTO_APPLIED_TAG = 'Approve auto-applied tag';
    const LABEL_REMOVE_TAG = 'Remove tag';

    const options: ActionDropdownType[] = [];

    if (tag.isAutoApplied && !tag.isExample) {
      options.push({
        label: LABEL_APPROVE_AUTO_APPLIED_TAG,
        icon: 'thumbs-up-outline',
      });
    }

    options.push({
      label: LABEL_REMOVE_TAG,
      icon: 'trash-outline',
      destructive: true,
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: async (_idx: number, label: string) => {
          popover.dismiss();

          if (label === LABEL_APPROVE_AUTO_APPLIED_TAG) {
            await this.conversationsService.approveTag(this.conversationId, tag.tagId);
          } else if (label === LABEL_REMOVE_TAG) {
            await this.tagsService.deleteConversationLinks(tag.tagId, [this.conversationId]);
          }

          this.loadTags();
        },
      },
    });
    popover.present();
  }

  formatEmail(email: any, isExpanded: boolean) {
    return (
      (isExpanded
        ? email.htmlContent
        : email.html
          ? email.html
          : email.textContent?.replaceAll('\n\n', '\n').replaceAll('\n', '<br/>')) ?? ''
    );
  }

  formatMicrosoftTeamsMessage(microsoftTeamsMessage: any, index: number) {
    return `${index === 0 && microsoftTeamsMessage.subject ? `<b>Subject:</b>&nbsp;${microsoftTeamsMessage.subject}<br/><br/>` : ''}${microsoftTeamsMessage.content
      .replaceAll('\n\n', '\n')
      .replaceAll('\n', '<br/>')}`;
  }

  async openEmailHtmlModal(email: any) {
    this.inboundEmail = email;
    this.showEmailHtmlModal = true;
  }

  onDismissEmailHtmlModal() {
    this.showEmailHtmlModal = false;
  }

  downloadWidgetMessageAttachment(widgetMessage: any, attachment: any) {
    window.open(`/dashboard/inbound-app-chat/${widgetMessage.id}?downloadFileId=${attachment.slackId}`, '_blank');
  }

  downloadEmailMessageAttachment(attachmentId: string) {
    downloadSlackAttachment(
      this.apiService,
      attachmentId,
      this.conversation?.slackChannel?.teamId ?? this.conversation.teamId,
      this.conversation.id,
    );
  }

  async showViewOptions() {
    const options: ActionDropdownType[] = [
      {
        label: 'Show audit trail',
        selected: this.showAuditTrail,
      },
      {
        label: 'Include low importance audit entries',
        selected: this.showAuditTrailLow,
      },
      {
        label: 'Show triage messages',
        selected: this.showTriageMessages,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        checkbox: true,
        multiCheckbox: true,
        callback: async (index: number, _label: string) => {
          if (index === 0) {
            this.showAuditTrail = !this.showAuditTrail;
            localStorage.setItem('showAuditTrail', this.showAuditTrail ? 'shown' : 'hidden');

            this.analyticsService.trackEvent(
              'Conversation Details',
              this.showAuditTrail ? 'Show Audit Trail' : 'Hide Audit Trail',
            );
          } else if (index === 1) {
            this.showAuditTrailLow = !this.showAuditTrailLow;
            localStorage.setItem('showAuditTrailLow', this.showAuditTrailLow ? 'shown' : 'hidden');

            this.analyticsService.trackEvent(
              'Conversation Details',
              this.showAuditTrailLow ? 'Show Low Importance Audit Trail' : 'Hide Low Importance Audit Trail',
            );
          } else if (index === 2) {
            this.showTriageMessages = !this.showTriageMessages;

            this.analyticsService.trackEvent(
              'Conversation Details',
              this.showTriageMessages ? 'Show Triage Messages' : 'Hide Triage Messages',
            );
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async toggleAnalytics() {
    this.analyticsService.trackEvent(
      'Conversation Details',
      this.conversation.excludeAnalytics ? 'Enable Analytics' : 'Exclude Analytics',
    );

    this.updateConversation({ excludeAnalytics: !this.conversation.excludeAnalytics });
  }

  async toggleLocked() {
    this.analyticsService.trackEvent(
      'Conversation Details',
      this.conversation.lockedAt ? 'Unlock Conversation' : 'Lock Conversation',
    );

    this.updateConversation({
      status: CONVERSATION_STATUS.CLOSED,
      lockedAt: this.conversation.lockedAt ? null : new Date(),
    });
  }

  taskManagerSupportsCreation() {
    return (
      this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.LINEAR ||
      this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.ASANA ||
      this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.CLICKUP ||
      this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.HEIGHT ||
      this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.JIRA ||
      this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.SHORTCUT ||
      this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.GITHUB
    );
  }

  async createTask() {
    const config = this.authService.tenant.externalTaskManagementConfig;
    const descriptPrefix = this.conversation.summary || 'Add description here... ';
    let description = `${descriptPrefix}\n\n\nSlack link: ${this.getSlackLinkForConversationMessage(
      this.messages[0].ts,
    )} Unthread conversation ${this.conversation.friendlyId}: ${window.location.href}`;
    const title = this.conversation.title;

    let link: string;
    let canOpenDirectly = false;

    if (this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.LINEAR) {
      link = `https://linear.app/new?title=${title}&description=${description}`;
      canOpenDirectly = true;
    } else if (this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.ASANA) {
      link = `https://app.asana.com/-/create_task?name=${title}&notes=${description}`;
      canOpenDirectly = true;
    } else if (this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.CLICKUP) {
      link = `https://app.clickup.com`;
    } else if (this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.HEIGHT) {
      link = `https://height.app`;
    } else if (this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.JIRA) {
      link = `${config.siteUrl}/secure/CreateIssue.jspa`;
    } else if (this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.SHORTCUT) {
      description = encodeURIComponent(
        `${descriptPrefix}\n\n\n\n[Slack thread](${this.getSlackLinkForConversationMessage(
          this.messages[0].ts,
        )})\n[Unthread conversation #${this.conversation.friendlyId}](${window.location.href})`,
      );

      link = `https://app.shortcut.com/stories/new?description=${description}`;
      canOpenDirectly = true;
    } else if (this.taskManagerName === EXTERNAL_TASK_MANAGEMENT_NAMES.GITHUB) {
      link = `https://github.com/${config.orgName}`;
    }

    if (!link) {
      this.msgSrvc.show(`We don't support this task manager yet`);
    } else if (canOpenDirectly) {
      window.open(link, '_blank');
    } else {
      const slackLink = this.getSlackLinkForConversationMessage(this.messages[0].ts);
      const alert = await this.alertCtrl.create({
        header: `Sending you to ${this.taskManagerName}`,
        subHeader: `Include this Slack link in your task description:`,
        cssClass: 'wide-alert-controller',
        inputs: [
          {
            name: 'code',
            value: slackLink,
            placeholder: '',
          },
        ],
        buttons: [
          {
            text: `Copy Link & Open ${this.taskManagerName}`,
            handler: async () => {
              await this.copyText(slackLink);

              window.open(link, '_blank');
            },
          },
        ],
      });
      alert.present();
    }
  }

  async loadApprovalRequestData() {
    try {
      const approvalRequests = await this.conversationsService.getApprovalRequests(this.conversationId);

      this.approvalRequests = approvalRequests;

      if (this.approvalRequests?.length && !this.openApprovalRequestsAccordions) {
        this.openApprovalRequestsAccordions = 'approvalRequests';
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  selectAutomationToRun() {
    this.searchablePopoverService.automations({
      event,
      trigger: {
        type: ['manual', 'slackReaction'],
      },
      showUnlisted: false,
      projectId: this.conversation.projectId,
      callback: async (id: string, _name: string, data: Automation) => {
        if (!id) {
          return;
        }
        const modal = await this.modalCtrl.create({
          component: TriggerManualAutomationModalComponent,
          componentProps: {
            automation: data,
            id: id,
            conversationId: this.conversationId,
          },
          cssClass: 'auto-height modal-sm',
        });
        modal.present();
        modal.onDidDismiss().then(async () => {
          this.getAutomationRuns();
        });
      },
    });
  }

  async getAutomationRuns() {
    if (!this.authService.tenant.isCustomActionsEnabled) {
      return;
    }

    try {
      const automationRunsResponse = await this.automationsService.listRunsForConversation(this.conversationId, {
        limit: null,
        order: ['startedAt'],
        descending: false,
      });

      this.automationRuns = automationRunsResponse.data;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async viewAutomationLogs(automationId: string, runId: string) {
    const modal = await this.modalCtrl.create({
      component: AutomationLogsComponent,
      cssClass: 'modal-large',
      componentProps: {
        runId,
        automationId,
      },
    });

    modal.present();
  }

  getWhereClauseForPreviousConversations() {
    const clause = [
      {
        field: 'id',
        operator: '!=',
        value: this.conversationId,
      },
    ];

    if (this.recentConversationsTab === 'account') {
      clause.push({
        field: 'customerId',
        operator: '==',
        value: this.conversation.customerId,
      });
    } else if (this.recentConversationsTab === 'user') {
      clause.push({
        field: 'initialMessage.sentByUserId',
        operator: '==',
        value: this.submittedBy?.id,
      });
    }

    return clause;
  }

  async getSimilarConversations() {
    this.analyticsService.trackEvent('Conversation Details', 'Expand Similar Conversations');

    if (!this.similarConversations?.length) {
      this.isLoadingSimilarConversations = true;
      this.similarConversations = await this.apiService.getPromise(`/conversations/${this.conversationId}/similar`);
      this.isLoadingSimilarConversations = false;
    }
  }

  async getEscalations(force?: boolean) {
    if (!this.escalations?.length || force) {
      this.isLoadingEscalations = true;

      try {
        const response = await this.escalationsService.list({
          where: [
            {
              field: 'conversationId',
              operator: '==',
              value: this.conversationId,
            },
          ],
        });

        this.escalations = response.data;

        if (this.escalations?.length && !this.openEscalationsAccordions) {
          this.openEscalationsAccordions = 'escalations';
        }
      } catch (err) {
        this.msgSrvc.showError(err);
      } finally {
        this.isLoadingEscalations = false;
      }
    }
  }

  getPreviousConversations(force?: boolean) {
    this.analyticsService.trackEvent('Conversation Details', 'Expand Previous Conversation');

    if (!this.submittedBy?.id) {
      this.recentConversationsTab = 'account';
    }

    if (!this.loadedPreviousConversations || force) {
      this.loadedPreviousConversations = true;
      this.previousConversationsComponent.whereClauses = this.getWhereClauseForPreviousConversations();
      this.previousConversationsComponent.loadConversations();
    }
  }

  getRouterPath() {
    return `/${routes.DASHBOARD}/${routes.INBOX}/all/conversations`;
  }

  focusComposeField() {
    this.composer.focus(true);
  }

  trackByFn(_i: number, message: any) {
    return message.id;
  }

  createFileAttachmentErrorHandler(ts: string) {
    return () => this.openMessageInSlack(ts);
  }

  async linkExternalTask() {
    const modal = await this.modalCtrl.create({
      component: LinkExternalTaskModalComponent,
      cssClass: 'modal-content-only',
      componentProps: {
        externalTaskManagementConfig: this.authService.tenant.externalTaskManagementConfig,
      },
    });

    modal.present();

    modal.onDidDismiss().then(async ({ data }: any) => {
      if (data?.externalTaskId) {
        this.isLoadingTasks = true;
        try {
          await this.conversationsService.linkToExternalTask(
            this.conversationId,
            data.externalTaskId,
            data.providerConfig,
          );

          this.loadTasks();
        } catch (e) {
          this.msgSrvc.showError(e);
          this.isLoadingTasks = false;
        }
      }
    });
  }

  async showTaskOptions(taskId: string) {
    const options: ActionDropdownType[] = [
      {
        label: 'Unlink task',
        icon: 'unlink-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: async (idx: number) => {
          popover.dismiss();

          await this.conversationsService.unlinkExternalTask(this.conversationId, taskId);
          this.loadConversation();
        },
      },
    });
    popover.present();
  }

  composerDidSendPendingMessage(data: any) {
    this.pendingMessages[data.ts] = {
      content: data.content,
      name: this.authService.user.displayName,
      isPrivateExplanation: data.isPrivateExplanation,
      attachments: data.attachments,
      ts: data.ts,
    };

    if (data.addSendingMessageId) {
      this.sendingMessageIds.add(data.ts);
    }

    this.composer.setEmailRecipients({ tos: [], ccs: [], bccs: [] });

    this.tos = [];
    this.ccs = [];
    this.bccs = [];

    setTimeout(() => {
      this.content.scrollToBottom(0);
    }, 50);
  }

  composerDidInsertKnowledgebaseArticle(article: Partial<KnowledgeBaseArticle>) {
    this.selectKnowledgeTab(article);
  }

  composerDidRunAutomation(_automation: Partial<Automation>) {
    this.openAutomationsAccordions = 'automations';
    this.getAutomationRuns();
  }

  async sendTestEmailToSelf(data: any) {
    const loader = await this.loadingCtrl.create({
      message: 'Sending test email...',
      spinner: 'crescent',
    });

    await loader.present();

    try {
      const { content, messageBlocks, userEmail, attachments } = data;

      // Prepare the special email data format that will be sent to the API
      const response = await this.apiService.postPromise(`/conversations/${this.conversation.id}/test-email`, {
        body: { type: 'html', value: content },
        blocks: messageBlocks,
        to: userEmail,
        subject: `[TEST] ${this.conversation.title || 'No subject'}`,
        attachments: attachments?.map((file: any) => ({
          id: file.id,
          name: file.name,
          url: file.urlPrivate,
          mimetype: file.mimetype,
        })),
      });

      this.msgSrvc.show('Test email sent to ' + userEmail);
    } catch (error) {
      this.msgSrvc.showError(error);
    } finally {
      loader.dismiss();
    }
  }

  kbSearchDidComposeResponse(data: any) {
    this.composer.insertTextContent(data.content);
  }

  openExternalTicket() {
    window.open(this.conversation.externalSupportMetadata?.url, '_blank');
  }

  async onFieldValuesUpdate(newValues: CustomFieldValues) {
    // Update local state immediately for UI responsiveness - handled by updateConversation
    this.conversation.ticketTypeFields = newValues;
    // this.ticketTypeFields = newValues; // Old way, not needed now

    // Make the API call directly
    this.updateConversation({ ticketTypeFields: newValues }, true);
  }

  async insertEmoji(messageId: string) {
    if (!this.hasUserReactionsWriteScope) {
      const alert = await this.alertCtrl.create({
        header: 'You need to reconnect to Slack to add emoji reactions.',
        buttons: [
          {
            text: 'Connect now',
            handler: async () => {
              this.connectUserToSlack();
            },
          },
        ],
      });

      alert.present();

      return;
    }

    const popover = await this.popoverCtrl.create({
      component: EmojiPickerComponent,
      componentProps: {
        callback: async (emoji: any) => {
          popover.dismiss();

          try {
            const emojiName = `${emoji.id}${emoji.skin != null && emoji.skin >= 2 ? `::skin-tone-${emoji.skin}` : ''}`;

            await this.conversationsService.addReactionToMessage(this.conversationId, messageId, emojiName);

            const message = this.messages.find((m: any) => m.id === messageId);

            if (message) {
              if (!message.reactions) {
                message.reactions = [];
              }

              const reaction = message.reactions.find((r: any) => r.name === emojiName);

              if (reaction) {
                reaction.count++;
                reaction.users.push(this.authService.profile.slackId);
              } else {
                message.reactions.push({
                  name: emojiName,
                  count: 1,
                  users: [this.authService.profile.slackId],
                });
              }

              this.messages = [...this.messages];

              this.setEmojiReactionTooltips();
            }

            this.analyticsService.trackEvent('Conversation Details', 'Insert Emoji Reaction');
          } catch (err) {
            this.msgSrvc.showError(err);
          }
        },
      },
      showBackdrop: false,
      cssClass: 'emoji-picker-popover',
      event,
    });

    popover.present();
  }

  async changeReaction(messageId: string, reaction: any) {
    if (!this.hasUserReactionsWriteScope) {
      return;
    }

    if (this.currentUserAddedReaction(reaction)) {
      await this.conversationsService.removeReactionFromMessage(this.conversationId, messageId, reaction.name);

      reaction.users = reaction.users.filter((u: string) => u !== this.authService.profile.slackId);
      reaction.count--;

      if (reaction.count === 0) {
        const message = this.messages.find((m: any) => m.id === messageId);

        if (message) {
          message.reactions = message.reactions.filter((r: any) => r.name !== reaction.name);
        }
      }
    } else {
      try {
        await this.conversationsService.addReactionToMessage(this.conversationId, messageId, reaction.name);

        reaction.users.push(this.authService.profile.slackId);
        reaction.count++;
      } catch (err) {
        this.msgSrvc.showError(err);
        return;
      }
    }

    this.messages = [...this.messages];

    this.setEmojiReactionTooltips();
  }

  currentUserAddedReaction(reaction: any) {
    return reaction.users.includes(this.authService.profile.slackId);
  }

  setEmojiReactionTooltips() {
    for (const message of this.messages) {
      if (message.reactions?.length) {
        for (const reaction of message.reactions) {
          if (reaction.users?.length) {
            let userNames = reaction.users.map((u: string) => {
              if (u === this.authService.profile.slackId) {
                return 'you';
              }

              if (u === this.authService.tenant.slackBotId) {
                return 'Unthread';
              }

              return this.lookups.userSlackId[u] ?? 'Bot';
            });

            if (userNames.includes('you')) {
              userNames = userNames.filter((name) => name !== 'you');
              userNames.push('you');
            }

            let userNamesStr =
              (userNames.length > 1
                ? userNames.slice(0, -1).join(', ') + ' and ' + userNames.slice(-1)
                : userNames[0]) ?? '';

            userNamesStr = userNamesStr.charAt(0).toUpperCase() + userNamesStr.slice(1);

            this.emojiReactionTooltipProps[`${message.id}::${reaction.name}`] = {
              touch: false,
              arrow: false,
              placement: 'top',
              content: `${userNamesStr} reacted with :${reaction.name}:`,
            };
          }
        }
      }
    }
  }

  async checkUserSlackScopes() {
    const [userSlackScopes, tenantSlackScopes] = await Promise.all([
      this.slackService.getUserScopes(this.conversation?.slackChannel?.teamId ?? this.conversation.teamId),
      this.slackService.getScopes(this.conversation?.slackChannel?.teamId ?? this.conversation.teamId),
    ]);

    this.hasUserChatWriteScope = userSlackScopes.exists && userSlackScopes.scopes['chat:write'];
    this.hasUserReactionsWriteScope = userSlackScopes.exists && userSlackScopes.scopes['reactions:write'];
    this.hasUserFilesWriteScope = userSlackScopes.exists && userSlackScopes.scopes['files:write'];
    this.hasTenantFilesWriteScope = tenantSlackScopes.exists && tenantSlackScopes.scopes['files:write'];
  }

  connectUserToSlack = async () => {
    if (this.isConnectingUserToSlack) {
      return;
    }

    this.isConnectingUserToSlack = true;

    let slackTeamId: string | null = this.conversation.slackChannel?.teamId ?? this.conversation.teamId;

    // HACK: Slack doesn't like it if we pass an enterprise id to the oauth flow. So try and determine a workspace id from the enterprise id.
    if (slackTeamId && slackTeamId.startsWith('E')) {
      const slackWorkspaceIds = (this.conversation.customer?.slackTeams ?? []).map((t) => t.id);

      // Find the first team that has a matching enterprise id
      const eligibleTemas = this.connectedTeams.filter(
        (t) => t.slackEnterpriseId === slackTeamId && slackWorkspaceIds.includes(t.id),
      );

      if (eligibleTemas.length === 1) {
        slackTeamId = eligibleTemas[0].id;
      } else {
        slackTeamId = null;
      }
    }

    this.slackService.userOauth(
      `${window.location.origin}/close-window`,
      window.location.origin,
      !this.connectedTeams.find((t) => t.id === (this.conversation.slackChannel?.teamId ?? this.conversation.teamId))
        ?.isPrimary,
      slackTeamId,
    );

    this.isLoadingConversation = true;

    await delay(5000);

    while (!this.hasUserChatWriteScope) {
      await this.checkUserSlackScopes();
      await delay(2000);
    }

    this.isConnectingUserToSlack = false;
    this.isLoadingConversation = false;
  };

  formatSlackContent(content: any[]) {
    return formatSlackContent(content);
  }

  async downloadAttachment(attachmentId: string) {
    const response: any = validateUuid(attachmentId)
      ? await this.apiService.download(`/conversations/${this.conversationId}/files/${attachmentId}/download`, 'GET')
      : await this.apiService.download(
          `/slack/files/${attachmentId}/download${this.conversation?.slackChannel?.teamId ?? this.conversation.teamId ? `?teamId=${this.conversation?.slackChannel?.teamId ?? this.conversation.teamId}` : ''}`,
          'GET',
        );

    const contentDisposition: string = response.headers.get('content-disposition');
    const filename =
      contentDisposition
        .split(';')
        .find((p) => p.startsWith('filename'))
        ?.split('=')[1] ?? 'file.txt';

    const binaryData = [];

    binaryData.push(response.body);

    const downloadLink = document.createElement('a');

    downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'blob' }));
    downloadLink.setAttribute('download', filename);

    document.body.appendChild(downloadLink);

    downloadLink.click();
    downloadLink.remove();
  }

  async addFollower(event?: Event) {
    this.searchablePopoverService.assignees({
      event,
      showCreatedByUserOption: false,
      showPrimaryRepOption: false,
      showNoneOption: false,
      callback: async (id: string, _name: string, object: any) => {
        if (id) {
          const updatedFollowers = await this.apiService.postPromise<any[]>(
            `/conversations/${this.conversationId}/add-follower`,
            {
              entityId: id ?? null,
              entityType: object.type ?? SUPPORT_STEP_ASSIGNEE_TYPES.USER,
            },
          );

          if (updatedFollowers) {
            this.conversation.followers = updatedFollowers;
          }

          this.msgSrvc.show('✔︎ Saved', { duration: 2000, position: 'bottom' });
        }
      },
    });
  }

  async showFollowerOptions(entityId: string) {
    const options: ActionDropdownType[] = [
      {
        label: 'Remove follower',
        icon: 'close-circle',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (index: number, _label: string) => {
          popover.dismiss();

          if (index === 0) {
            const updatedFollowers = await this.apiService.postPromise<any[]>(
              `/conversations/${this.conversationId}/remove-follower`,
              {
                entityId,
              },
            );

            if (updatedFollowers) {
              this.conversation.followers = updatedFollowers;
            }

            this.msgSrvc.show('✔︎ Saved', { duration: 2000, position: 'bottom' });
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  showCollaboratorOptions(collaboratorTypeId: string) {
    this.searchablePopoverService.assignees({
      event,
      selectedId: this.collaborators[collaboratorTypeId]?.entityId,
      showCreatedByUserOption: false,
      showPrimaryRepOption: false,
      showNoneOption: true,
      callback: async (id: string, _name: string, object: any) => {
        this.analyticsService.trackEvent('Conversation Details', 'Change Collaborator');

        try {
          this.isLoadingConversation = true;

          const updatedCollaborators = await this.apiService.putPromise<ConversationCollaborator[]>(
            `/conversations/${this.conversationId}/collaborators/${collaboratorTypeId}`,
            {
              entityId: id ?? null,
              entityType: object.type ?? SUPPORT_STEP_ASSIGNEE_TYPES.USER,
            },
          );

          if (updatedCollaborators) {
            this.conversation.collaborators = updatedCollaborators;
            this.collaborators = updatedCollaborators.reduce(
              (acc, c) => {
                if (c && c.collaboratorType && c.collaboratorType.id) {
                  acc[c.collaboratorType.id] = c;
                }
                return acc;
              },
              {} as Record<string, ConversationCollaborator>,
            );
          } else {
            this.conversation.collaborators = [];
            this.collaborators = {};
          }

          this.msgSrvc.show('✔︎ Saved', { duration: 2000, position: 'bottom' });

          await this.loadAuditLog();
          this.timelineEvents = this.buildTimelineEvents();
        } catch (error) {
          this.msgSrvc.show('Failed to update collaborator', { duration: 2000, position: 'bottom' });
        } finally {
          this.isLoadingConversation = false;
        }
      },
    });
  }

  getApprovalRequestLabel(approvalRequest: Partial<ApprovalRequest>) {
    const approverGroupName = approvalRequest.approverGroup?.name;
    const approverUserName = approvalRequest.approverUser?.name;
    const sentToName = approverGroupName ?? approverUserName;

    if (approvalRequest.status === 'pending') {
      return `${sentToName ? ` sent to ${sentToName}` : ''}`;
    }

    return `${approverUserName ? ` by ${approverUserName}` : ''}`;
  }

  async openCreateApprovalRequestModal() {
    const modal = await this.modalCtrl.create({
      component: ApprovalRequestComponent,
      cssClass: 'modal-content-only modal-sm',
      componentProps: {
        conversationId: this.conversationId,
      },
    });

    modal.present();

    modal.onDidDismiss().then(async ({ data }: any) => {
      if (data?.approvalRequest) {
        this.analyticsService.trackEvent('Conversation Details', 'Manually Create Approval Request');

        this.loadApprovalRequestData();
      }
    });
  }

  getPendingMessages() {
    return Object.values(this.pendingMessages);
  }

  pendingMessagesTrackBy(message: any) {
    return message.ts;
  }

  emojiReactionsTrackBy(reaction: any) {
    return reaction.name;
  }

  fileTrackBy(file: any) {
    return file.id;
  }

  filteredTriageMessages() {
    return this.conversation.triageMessages?.filter((t: any) => t.channelName && !t.isCustomerFacing);
  }

  getReactionEmoji(emojiName: string) {
    const [name, skinTone] = emojiName.split('::');
    const skinToneIndexStr = skinTone ? skinTone.replace('skin-tone-', '') : '0';
    const skinToneIndex = parseInt(skinToneIndexStr, 10);

    const emoji = Data?.emojis[name]?.skins?.[skinToneIndex];

    return emoji?.native ?? emoji?.src ?? `:${name}:`;
  }

  toggleEmailRecipients(data: any) {
    data.showEmailRecipients = !data.showEmailRecipients;
  }

  formatRecipient(recipient: any): string {
    if (typeof recipient === 'string') {
      return recipient;
    }

    return recipient.name?.trim() || recipient.email;
  }

  getCompactRecipientList(inboundEmail: any): string {
    const allRecipients = [...(inboundEmail.tos || []), ...(inboundEmail.ccs || []), ...(inboundEmail.bccs || [])];
    const formattedRecipients = allRecipients.map((r) => this.formatRecipient(r));

    if (formattedRecipients.length <= 2) {
      return formattedRecipients.join(', ');
    }

    return `${formattedRecipients[0]}, ${formattedRecipients[1]}, +${formattedRecipients.length - 2}`;
  }

  async presentRecipientsPopover(event: Event, inboundEmail: any) {
    const popover = await this.popoverCtrl.create({
      component: RecipientsPopoverComponent,
      componentProps: { inboundEmail },
      cssClass: 'extra-wide-popover',
      event: event,
      translucent: true,
      showBackdrop: false,
    });
    return await popover.present();
  }

  enableConversationLinks() {
    this.conversationLinksEnabled = true;
  }

  /**
   * Handles a new message being created
   */
  handleMessageCreated = _.debounce(async (data: any) => {
    // Fetch only the new message and append it to the existing list
    try {
      await this.loadMessages(false);
      this.timelineEvents = this.buildTimelineEvents();

      // Scroll to the new message
      setTimeout(() => this.content.scrollToBottom(300), 100);
    } catch (error) {
      console.error('Error handling message created event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles a triage message being created
   */
  handleTriageMessageCreated = _.debounce(async (data: any) => {
    try {
      await Promise.all([this.loadConversation(), this.loadMessages(false)]);
      this.timelineEvents = this.buildTimelineEvents();
    } catch (error) {
      console.error('Error handling triage message created event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles a message being updated
   */
  handleMessageUpdated = _.debounce(async (data: any) => {
    try {
      const messageId = data.data.id;

      const updatedMessage = await this.conversationsService.getMessage(this.conversationId, messageId);

      const messageIndex = this.messages.findIndex((m) => m.id === messageId);

      if (messageIndex !== -1) {
        this.messages[messageIndex] = updatedMessage;

        this.timelineEvents = this.buildTimelineEvents();
      } else {
        await this.loadMessages(false);
        this.timelineEvents = this.buildTimelineEvents();
      }
    } catch (error) {
      console.error('Error handling message updated event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles a message being deleted
   */
  handleMessageDeleted = _.debounce(async (data: any) => {
    try {
      const messageId = data.data.id;
      this.deletingMessageIds.delete(messageId);

      // Remove the message from the array
      this.messages = this.messages.filter((m) => m.id !== messageId);
      this.timelineEvents = this.buildTimelineEvents();
    } catch (error) {
      console.error('Error handling message deleted event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles the conversation status being changed
   */
  handleStatusChanged = _.debounce(async (data: any) => {
    try {
      // Update just the conversation status field
      const updatedConversation = await this.conversationsService.get(this.conversationId);

      if (updatedConversation) {
        this.conversation.status = updatedConversation.status;
        this.conversation.statusUpdatedAt = updatedConversation.statusUpdatedAt;
        this.conversation.subStatusId = updatedConversation.subStatusId;
        this.conversation.subStatus = updatedConversation.subStatus;
        this.conversation.closedAt = updatedConversation.closedAt;

        // Load the audit log to include the status change
        await this.loadAuditLog();
        this.timelineEvents = this.buildTimelineEvents();
      }
    } catch (error) {
      console.error('Error handling status changed event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles assignment changes (reassignment and unassignment)
   */
  handleAssignmentChanged = _.debounce(async (data: any) => {
    try {
      // Update just the assignment fields
      const updatedConversation = await this.conversationsService.get(this.conversationId);

      if (updatedConversation) {
        this.conversation.assignedToUserId = updatedConversation.assignedToUserId;
        this.conversation.assignedToUser = updatedConversation.assignedToUser;

        // Load the audit log to include the assignment change
        await this.loadAuditLog();
        this.timelineEvents = this.buildTimelineEvents();
      }
    } catch (error) {
      console.error('Error handling assignment changed event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles customer association changes
   */
  handleCustomerChanged = _.debounce(async (data: any) => {
    try {
      // Update customer information and load related data
      const updatedConversation = await this.conversationsService.get(this.conversationId);

      if (updatedConversation) {
        this.conversation.customerId = updatedConversation.customerId;
        this.conversation.customer = updatedConversation.customer;

        // Load the audit log to include the customer change
        await Promise.all([this.loadAuditLog(), this.getPreviousConversations(true)]);

        this.timelineEvents = this.buildTimelineEvents();
      }
    } catch (error) {
      console.error('Error handling customer changed event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles account association changes
   */
  handleAccountChanged = _.debounce(async (data: any) => {
    try {
      // Update account information and load related data
      const updatedConversation = await this.conversationsService.get(this.conversationId);

      if (updatedConversation) {
        this.conversation.accountId = updatedConversation.accountId;
        this.conversation.account = updatedConversation.account;

        // Load the audit log to include the account change
        await Promise.all([this.loadAuditLog(), this.getPreviousConversations(true)]);

        this.timelineEvents = this.buildTimelineEvents();
      }
    } catch (error) {
      console.error('Error handling account changed event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles followers being added or removed
   */
  handleFollowersChanged = _.debounce(async (data: any) => {
    try {
      // Update followers list
      const updatedConversation = await this.conversationsService.get(this.conversationId);

      if (updatedConversation) {
        this.conversation.followers = updatedConversation.followers;

        // Load the audit log to include the follower change
        await this.loadAuditLog();
        this.timelineEvents = this.buildTimelineEvents();
      }
    } catch (error) {
      console.error('Error handling follower change event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles collaborator being added or removed
   */
  handleCollaboratorChanged = _.debounce(async (data: any) => {
    try {
      // Update collaborators list
      const updatedConversation = await this.conversationsService.get(this.conversationId);

      if (updatedConversation) {
        this.conversation.collaborators = updatedConversation.collaborators;
        this.collaborators =
          this.conversation.collaborators?.reduce(
            (acc, c) => {
              acc[c.collaboratorType.id] = c;
              return acc;
            },
            {} as Record<string, ConversationCollaborator>,
          ) ?? {};

        // Load the audit log to include the collaborator change
        await this.loadAuditLog();
        this.timelineEvents = this.buildTimelineEvents();
      }
    } catch (error) {
      console.error('Error handling collaborator change event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles tags being added or removed
   */
  handleTagsChanged = _.debounce(async (data: any) => {
    try {
      // Update tags
      await this.loadTags();

      // Load the audit log to include the tag change
      await this.loadAuditLog();
      this.timelineEvents = this.buildTimelineEvents();
    } catch (error) {
      console.error('Error handling tags changed event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles the conversation being triaged
   */
  handleTriaged = _.debounce(async (data: any) => {
    try {
      // Load both the conversation and its messages (including triage messages)
      await Promise.all([this.loadConversation(), this.loadMessages(false)]);

      // Get filtered triage messages that have a channel name and are not customer facing
      const triageMessages = this.filteredTriageMessages();

      // Set the reply composer to use the most recent triage thread if available
      if (triageMessages?.length) {
        // Sort by timestamp to get the most recent one
        const sortedTriageMessages = [...triageMessages].sort(
          (a, b) => new Date(b.timestamp).valueOf() - new Date(a.timestamp).valueOf(),
        );

        const mostRecentTriageMessage = sortedTriageMessages[0];
        if (mostRecentTriageMessage) {
          this.composer.replyingToThreadId = mostRecentTriageMessage.ts;
          this.composer.replyingToThreadLabel = mostRecentTriageMessage.channelName;
        }
      }

      // Load the audit log to include the triage action
      await this.loadAuditLog();
      this.timelineEvents = this.buildTimelineEvents();
    } catch (error) {
      console.error('Error handling triaged event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles the conversation being snoozed
   */
  handleSnoozed = _.debounce(async (data: any) => {
    try {
      // Update snooze status
      const updatedConversation = await this.conversationsService.get(this.conversationId);

      if (updatedConversation) {
        // Check if snoozedUntil exists in the updated conversation
        if ('snoozedUntil' in updatedConversation) {
          // Use type assertion to handle the property
          (this.conversation as any).snoozedUntil = updatedConversation.snoozedUntil;
        }

        // Load the audit log to include the snooze action
        await this.loadAuditLog();
        this.timelineEvents = this.buildTimelineEvents();
      }
    } catch (error) {
      console.error('Error handling snoozed event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles approval request changes
   */
  handleApprovalRequestsChanged = _.debounce(async (data: any) => {
    try {
      // Update approval requests
      await this.loadApprovalRequestData();

      // Load the audit log to include the approval action
      await this.loadAuditLog();
      this.timelineEvents = this.buildTimelineEvents();
    } catch (error) {
      console.error('Error handling approval request changed event', error);
      await this.fetchTimeline(false, false);
    }
  }, 300);

  /**
   * Handles the conversation being deleted
   */
  handleDeleted = _.debounce(() => {
    // Show a notification and navigate away
    this.msgSrvc.show('This conversation has been deleted', { duration: 3000 });
    this.dismiss();
  }, 300);

  /**
   * Rebuilds the timeline events from messages and audit logs
   */
  private buildTimelineEvents(): TimelineEvent[] {
    const userSlackIds = new Set<string>();
    let shouldLoadApprovals = false;

    const events = this.messages
      .map((m) => {
        if (m.reactions?.length) {
          for (const reaction of m.reactions) {
            if (reaction.users?.length) {
              for (const user of reaction.users) {
                userSlackIds.add(user);
              }
            }
          }
        }

        return {
          type: 'message',
          timestamp: new Date(m.timestamp),
          data: transformMessage(m),
        };
      })
      .concat(
        this.auditLog.map((a) => {
          if (a.type === 'approval_request_created') {
            shouldLoadApprovals = true;
          }

          return {
            type: 'auditItem',
            timestamp: new Date(a.timestamp),
            data: a,
          };
        }),
      )
      .concat(
        this.linkedConversations.map((link) => {
          const [linkedConversationId, unidirectionalTypeIndex] =
            link.linkedConversationId === this.conversationId
              ? [link.conversationId, 1]
              : [link.linkedConversationId, 0];

          const unidirectionalType = CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_MAP[link.type][unidirectionalTypeIndex];

          return {
            type: 'conversationLink',
            timestamp: new Date(link.createdAt),
            data: {
              icon: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_ICONS[unidirectionalType],
              label: CONVERSATION_LINK_TYPES_UNIDIRECTIONAL_LABELS[unidirectionalType],
              type: unidirectionalType,
              linkedConversationId,
            },
          };
        }),
      )
      .sort((a: any, b: any) => {
        // HACK: For bot dms, the initial message can come after the auto response in the list so we want to re-arrange them.
        if (a.type === 'message' && a.data.id === this.conversation.initialMessageId) {
          return -1;
        }

        if (b.type === 'message' && b.data.id === this.conversation.initialMessageId) {
          return 1;
        }

        if (a.timestamp < b.timestamp) {
          return -1;
        } else if (b.timestamp < a.timestamp) {
          return 1;
        }

        return 0;
      })
      // For bot dms, we want to filter out the triage style message that starts the main thread.
      .filter((e) => !(this.isBotDmBased && e.type === 'message' && e.data.id === this.conversation.initialMessageId));

    // If we found approval requests, make sure we load them
    if (shouldLoadApprovals && !this.approvalRequests.length) {
      this.loadApprovalRequestData();
    }

    return events;
  }

  scrollToBottom() {
    this.content.scrollToBottom(300);
  }

  /**
   * Handles any conversation updates
   */
  handleConversationUpdated = _.debounce(async (data: any) => {
    try {
      const updatedConversationFromServer = await this.conversationsService.get(this.conversationId);

      if (updatedConversationFromServer) {
        this.conversation = updatedConversationFromServer;

        this.collaborators =
          this.conversation.collaborators?.reduce(
            (acc, c) => {
              if (c && c.collaboratorType && c.collaboratorType.id) {
                acc[c.collaboratorType.id] = c;
              }
              return acc;
            },
            {} as Record<string, ConversationCollaborator>,
          ) ?? {};

        this._calculateAllFieldDefinitions();
      }

      //  Most "column" updates on conversations generate an audit log entry.
      //  We don't have an event for new audit log entries.
      //  Reload audit log to show the changes.
      await this.loadAuditLog();
      this.timelineEvents = this.buildTimelineEvents();

      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error handling conversation update event', error);
      await this.fetchTimeline(false, false);
      this.cdr.detectChanges();
    }
  }, 300);

  handleEscalationCreated = _.debounce(async (_data: any) => {
    try {
      await this.getEscalations(true);
      await this.loadAuditLog();

      this.timelineEvents = this.buildTimelineEvents();
    } catch (error) {
      console.error('Error handling escalation created event', error);

      await this.fetchTimeline(false, false);
    }
  }, 300);

  handleEscalationUpdated = _.debounce(async (data: any) => {
    try {
      const escalation = await this.escalationsService.getById(data.data.escalationId);

      if (escalation) {
        escalation.status = escalation.status;
        escalation.priority = escalation.priority;
        escalation.assignedToUserId = escalation.assignedToUserId;
        escalation.assignedToGroupId = escalation.assignedToGroupId;
      }

      await this.loadAuditLog();

      this.timelineEvents = this.buildTimelineEvents();
    } catch (error) {
      console.error('Error handling escalation updated event', error);

      await this.fetchTimeline(false, false);
    }
  }, 300);

  isDev() {
    return this.authService.isSuperAdmin || isDev();
  }

  isViewAgentThoughtsEnabled() {
    return this.authService.tenant.viewAgentThoughtsEnabled || this.isDev();
  }

  copyFriendlyId() {
    navigator.clipboard.writeText(this.conversation.friendlyId);
    this.msgSrvc.show('Copied ID to clipboard', { duration: 2000, position: 'top' });
  }

  async changeEscalationStatus(escalation: Partial<Escalation>, event: Event) {
    this.searchablePopoverService.escalationStatuses({
      event,
      selectedStatusId: escalation.status,
      callback: async (id: string) => {
        if (id && id !== escalation.status) {
          await this.updateEscalation(escalation, { status: id });
        }
      },
    });
  }

  async initializePriorityTokens() {
    this.priorityTokens = (
      await this.tokenService.getResolvedTokens(['PRIORITY_LABEL_MAP'], this.conversation.customerId)
    )['PRIORITY_LABEL_MAP'];
    this.priorityValueToTokenMap = createPriorityValueToTokenMap(this.priorityTokens);
  }

  async changeEscalationPriority(escalation: Partial<Escalation>, event: Event) {
    const options: ActionDropdownType[] = [
      {
        label: this.priorityTokens.LOW.LABEL,
        emoji: this.priorityTokens.LOW.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.LOW),
      },
      {
        label: this.priorityTokens.MEDIUM.LABEL,
        emoji: this.priorityTokens.MEDIUM.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.MEDIUM),
      },
      {
        label: this.priorityTokens.HIGH.LABEL,
        emoji: this.priorityTokens.HIGH.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.HIGH),
      },
      {
        label: this.priorityTokens.CRITICAL.LABEL,
        emoji: this.priorityTokens.CRITICAL.EMOJI,
        icon: this.getIconForPriority(CONVERSATION_PRIORITY_VALUES.CRITICAL),
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (idx: number) => {
          popover.dismiss();

          let priority: number | null;

          switch (idx) {
            case 0:
              priority = CONVERSATION_PRIORITY_VALUES.LOW;
              break;
            case 1:
              priority = CONVERSATION_PRIORITY_VALUES.MEDIUM;
              break;
            case 2:
              priority = CONVERSATION_PRIORITY_VALUES.HIGH;
              break;
            case 3:
              priority = CONVERSATION_PRIORITY_VALUES.CRITICAL;
              break;
          }

          if (priority !== escalation.priority) {
            await this.updateEscalation(escalation, { priority });
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async updateEscalation(escalation: Partial<Escalation>, changes: Partial<Escalation>) {
    try {
      const updatedEscalation = await this.escalationsService.updatePartial(escalation.id, changes);
      // Update the local escalation object
      const index = this.escalations.findIndex((e) => e.id === escalation.id);

      if (index !== -1) {
        this.escalations[index] = { ...this.escalations[index], ...updatedEscalation };
        this.escalations = [...this.escalations]; // Trigger change detection
      }

      this.msgSrvc.show('✔︎ Escalation updated', { duration: 2000, position: 'bottom' });
    } catch (error) {
      this.msgSrvc.showError(error);
    }
  }

  async changeEscalationAssignee(escalation: Partial<Escalation>, event: Event) {
    this.searchablePopoverService.assignees({
      event,
      // Pass current user or group id if available for selection state
      selectedId: escalation.assignedToGroupId ?? escalation.assignedToUserId,
      showPrimaryRepOption: false,
      showCreatedByUserOption: false,
      includeNonAgents: true, // Include all users potentially
      showNoneOption: true,
      includeGroups: true,
      callback: async (id: string, name: string, object: any) => {
        if (object.type === SUPPORT_STEP_ASSIGNEE_TYPES.USER || !id) {
          // User selected OR 'No one' selected
          await this.updateEscalation(escalation, {
            assignedToUserId: id,
            assignedToGroupId: null,
            // Add assignedToAutoAssignFromGroup: false if your backend needs it
          });
        } else if (object.type === SUPPORT_STEP_ASSIGNEE_TYPES.TEAM) {
          // Group selected, proceed to step 2
          const selectedGroupId = id;
          await this.promptForUserInGroup(escalation, selectedGroupId, event);
        }
      },
    });
  }

  async promptForUserInGroup(escalation: Partial<Escalation>, groupId: string, event: Event) {
    this.searchablePopoverService.assignees({
      event,
      // Pass current user ID if it matches the group context
      selectedId: escalation.assignedToGroupId === groupId ? escalation.assignedToUserId : undefined,
      showAutoAssignFromGroupOption: true,
      showNoneOption: true,
      includeNonAgents: true,
      includeGroups: false,
      showPrimaryRepOption: false,
      showCreatedByUserOption: false,
      userGroupId: groupId,
      callback: async (selectedUserId: string, name: string, object: any) => {
        await this.updateEscalation(escalation, {
          assignedToUserId: selectedUserId,
          assignedToGroupId: groupId,
        });
      },
    });
  }

  get allFieldDefinitions(): CustomField[] {
    return this._allFieldDefinitions;
  }

  private _calculateAllFieldDefinitions(): void {
    if (!this.conversation) {
      this._allFieldDefinitions = [];
      return;
    }

    const definitions: CustomField[] = [];
    const seenIds = new Set<string>();

    // Add project fields
    if (this.conversation.project?.fields && Array.isArray(this.conversation.project.fields)) {
      (this.conversation.project.fields as CustomField[]).forEach((field) => {
        if (field && field.id && !seenIds.has(field.id)) {
          definitions.push(field);
          seenIds.add(field.id);
        }
      });
    }

    // Add ticket type specific fields
    if (this.conversation.ticketType?.fields && Array.isArray(this.conversation.ticketType.fields)) {
      (this.conversation.ticketType.fields as CustomField[]).forEach((field) => {
        if (field && field.id && !seenIds.has(field.id)) {
          definitions.push(field);
          seenIds.add(field.id);
        }
      });
    }
    this._allFieldDefinitions = definitions;
  }
}
